// Modern ERP Dashboard — KPI-led with sales / debtors / creditors / stock / approvals at a glance.
import { useEffect, useRef, useState, Fragment } from "react";
import { toast, Toaster } from "sonner";
import { api } from "@/lib/api";
import { PageHeader, StatusBadge } from "@/components/ui-helpers";
import { SearchableSelect } from "@/components/SearchableSelect";
import { useTabs } from "@/contexts/TabsContext";
import { useAuth } from "@/contexts/AuthContext";
import {
  TrendingUp, AlertCircle, Wallet, Package, FolderKanban, Users, HardHat,
  Briefcase, Inbox, Receipt, ArrowUpRight, RefreshCw, Search, X,
  Calculator, TrendingDown, Landmark, CheckCircle2, CircleDashed, CircleAlert,
  Banknote, Warehouse, Wrench, Activity, ShieldAlert, Lock, ClipboardList,
  Download, Hourglass, EyeOff, CalendarDays, ChevronDown, ChevronRight,
} from "lucide-react";

// Dashboard-local INR — KPI tiles are tight, so we drop the decimals (whole
// rupees only). Negative numbers preserved.
const INR = (n) => {
  const num = Number(n) || 0;
  const abs = Math.abs(num);
  return `${num < 0 ? "-" : ""}₹ ${abs.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
};

// Jun-2026 (perf) — client cache key for the instant-paint dashboard bundle.
const DASH_CACHE_KEY = "radiants_dash_cache_v1";

const timeAgo = (iso) => {
  try {
    const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)} min ago`;
    if (s < 86400) return `${Math.floor(s / 3600)} hr ago`;
    return `${Math.floor(s / 86400)} d ago`;
  } catch { return ""; }
};

// Small per-card refresh button — re-fetches ONLY that dashboard section.
function SectionRefreshBtn({ onRefresh, testid, dark = false }) {
  const [busy, setBusy] = useState(false);
  return (
    <button
      type="button"
      data-testid={testid}
      title="Refresh this section only"
      disabled={busy}
      onClick={async () => { setBusy(true); try { await onRefresh(); } finally { setBusy(false); } }}
      className={`w-6 h-6 rounded-md border flex items-center justify-center shrink-0 transition-colors disabled:opacity-50 ${
        dark ? "border-white/20 text-blue-200 hover:bg-white/10" : "border-slate-200 bg-white text-slate-400 hover:text-slate-700 hover:bg-slate-100"}`}
    >
      <RefreshCw size={11} className={busy ? "animate-spin" : ""} />
    </button>
  );
}

export default function Dashboard() {
  const { hasPerm } = useAuth();
  const canView = hasPerm("dashboard", "view");

  const [k, setK] = useState(null);
  const [costCtl, setCostCtl] = useState(null);
  // Sprint 4.4 — Warehouse dashboard widgets + Health tile
  const [whWidgets, setWhWidgets] = useState(null);
  const [whHealth,  setWhHealth]  = useState(null);
  // 2026-Feb — ONE (Official Note Entry) shortcut tile.
  // We fetch the summary independently so a failure here doesn't
  // break the rest of the dashboard load, and non-ONE users simply
  // see a zero (or the tile hides itself if the API 403s).
  const [oneSummary, setOneSummary] = useState(null);
  // Jun-2026 — Creditors Outstanding drill-down modal
  const [showCreditors, setShowCreditors] = useState(false);
  // Sep-2026 — 3-month SI strip drill-down (day-wise + client-wise)
  const [siDrill, setSiDrill] = useState(null); // {ym, label}
  const [loading, setLoading] = useState(canView);
  const [refreshing, setRefreshing] = useState(false);
  const [company, setCompany] = useState(null);
  // Jun-2026 (perf) — "Updated X ago" stamp + snapshot indicator
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isSnapshot, setIsSnapshot] = useState(false);
  const { openTab } = useTabs();

  const saveCache = (bundle) => {
    try { localStorage.setItem(DASH_CACHE_KEY, JSON.stringify(bundle)); } catch { /* quota */ }
  };

  const fetchData = (isRefresh = false) => {
    if (!canView) return Promise.resolve();        // Sprint 4.11.7 — no view perm → never hit the API
    if (isRefresh) setRefreshing(true);
    return Promise.all([
      api.get("/dashboard/kpis"),
      api.get("/company-settings").catch(() => ({ data: null })),
      api.get("/dashboard/project-cost-control").catch(() => ({ data: null })),
      // Sprint 4.4 Phase D — Warehouse widgets are independent of finance
      // KPIs, so a failure here must NOT crash the rest of the dashboard.
      api.get("/dashboard/warehouse-widgets").catch(() => ({ data: null })),
      // Health tile uses the existing admin-only API; fall back silently
      // for non-admin users (the tile then hides itself).
      api.get("/warehouses/health").catch(() => ({ data: null })),
      // ONE (Official Note Entry) — pending-with-me shortcut on the
      // main dashboard. Non-admin users without one.view see a null,
      // and the shortcut tile hides itself.
      api.get("/one/dashboard/summary").catch(() => ({ data: null })),
    ])
      .then(([kpi, cs, cc, wh, wHealth, oneS]) => {
        freshDoneRef.current = true;
        setK(kpi.data);
        setCompany(cs.data);
        setCostCtl(cc.data);
        setWhWidgets(wh.data);
        setWhHealth(wHealth.data);
        setOneSummary(oneS.data);
        const stamp = kpi.data?.generated_at || new Date().toISOString();
        setLastUpdated(stamp);
        setIsSnapshot(false);
        saveCache({ k: kpi.data, company: cs.data, costCtl: cc.data,
                    whWidgets: wh.data, whHealth: wHealth.data,
                    oneSummary: oneS.data, savedAt: stamp });
        if (isRefresh) toast.success("Dashboard refreshed");
      })
      .catch(() => {
        if (isRefresh) toast.error("Could not refresh dashboard");
      })
      .finally(() => {
        setLoading(false);
        setRefreshing(false);
      });
  };

  // Jun-2026 (perf) — segment-wise refresh: re-fetch ONLY the given
  // sections and merge the returned fields into the current KPI state.
  const refreshSections = async (sectionsCsv) => {
    try {
      const r = await api.get("/dashboard/kpis", { params: { sections: sectionsCsv } });
      setK((prev) => {
        const merged = { ...(prev || {}), ...r.data };
        try {
          const raw = localStorage.getItem(DASH_CACHE_KEY);
          if (raw) { const b = JSON.parse(raw); b.k = merged; localStorage.setItem(DASH_CACHE_KEY, JSON.stringify(b)); }
        } catch { /* cache best-effort */ }
        return merged;
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Section refresh failed");
    }
  };

  // Jun-2026 (perf) — refreshers for sections backed by their own endpoints.
  const patchCache = (patch) => {
    try {
      const raw = localStorage.getItem(DASH_CACHE_KEY);
      if (raw) { const b = JSON.parse(raw); Object.assign(b, patch); localStorage.setItem(DASH_CACHE_KEY, JSON.stringify(b)); }
    } catch { /* best-effort */ }
  };
  const refreshCostCtl = async () => {
    try {
      const r = await api.get("/dashboard/project-cost-control");
      setCostCtl(r.data);
      patchCache({ costCtl: r.data });
    } catch { toast.error("Section refresh failed"); }
  };
  const refreshWarehouse = async () => {
    const [w, h] = await Promise.all([
      api.get("/dashboard/warehouse-widgets").catch(() => ({ data: null })),
      api.get("/warehouses/health").catch(() => ({ data: null })),
    ]);
    setWhWidgets(w.data);
    setWhHealth(h.data);
    patchCache({ whWidgets: w.data, whHealth: h.data });
  };

  const freshDoneRef = useRef(false);
  useEffect(() => {
    if (!canView) return;
    // 1) Instant paint from the browser cache (last loaded data).
    let painted = false;
    try {
      const raw = localStorage.getItem(DASH_CACHE_KEY);
      if (raw) {
        const b = JSON.parse(raw);
        if (b && b.k) {
          setK(b.k); setCompany(b.company); setCostCtl(b.costCtl);
          setWhWidgets(b.whWidgets); setWhHealth(b.whHealth); setOneSummary(b.oneSummary);
          setLastUpdated(b.savedAt || null);
          setIsSnapshot(true);
          setLoading(false);
          painted = true;
        }
      }
    } catch { /* corrupt cache — ignore */ }
    // 2) No browser cache (new device / first login) → server snapshot
    //    paints in milliseconds while the fresh compute runs behind.
    if (!painted) {
      api.get("/dashboard/kpis", { params: { prefer: "snapshot" } })
        .then((r) => {
          if (freshDoneRef.current || !r.data) return;   // fresh already landed
          setK(r.data);
          setLastUpdated(r.data.snapshot_at || null);
          setIsSnapshot(!!r.data.is_snapshot);
          setLoading(false);
        })
        .catch(() => {});
    }
    // 3) Always refresh in the background on every open.
    fetchData(false);
    // 4) Aug-2026 (user request) — silent auto-refresh every 120 minutes so
    //    numbers stay live without any clicks (no toasts, stamp updates).
    const timer = setInterval(() => fetchData(false), 120 * 60 * 1000);
    return () => clearInterval(timer);
    // eslint-disable-next-line
  }, []);

  // Sprint 4.11.7 — Dashboard.view permission gate.
  // Roles where the admin has un-ticked the `dashboard.view` permission
  // checkbox land here. We deliberately keep the page reachable (it's
  // wired up as the landing route) but zero out every figure and render
  // a clear "restricted access" banner instead of leaking numbers.
  if (!canView) return <DashboardRestrictedPlaceholder />;

  if (loading) return <div className="text-center text-slate-500 py-20">Loading dashboard…</div>;
  if (!k) return <div className="text-center text-slate-500 py-20">Could not load KPIs.</div>;

  // Jul-2026 — per-user dashboard section restrictions (admin-managed in
  // Users & Roles → Dashboard Access). Backend strips restricted data;
  // here we hide the matching widgets and show a small note.
  const show = (key) => !k.dashboard_restricted || (k.dashboard_sections_allowed || []).includes(key);
  const maxMonthly = Math.max(1, ...(k.monthly_sales || []).map(m => m.amount));
  const maxFY = Math.max(1, ...(k.fy_sales || []).map(f => f.amount));

  return (
    <div data-testid="dashboard-page">
      <Toaster richColors position="top-right" />
      {/* Header row — welcome lock-up now lives in the global topbar (Layout.jsx).
          Here we keep only the Refresh action and the Pending Approvals KPI tile. */}
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-bold inline-flex items-center gap-2">Operations Snapshot · {new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
          {show("kpi_tiles") && <SectionRefreshBtn testid="refresh-kpi-tiles" onRefresh={() => refreshSections("kpi_tiles")} />}
        </div>
        <div className="flex items-center gap-3 flex-wrap w-full sm:w-auto">
          <div className="text-right" data-testid="dashboard-updated-stamp">
            <div className="text-[10px] text-slate-400 font-bold whitespace-nowrap">
              {refreshing ? (
                <span className="inline-flex items-center gap-1 text-blue-500"><RefreshCw size={10} className="animate-spin" /> Refreshing…</span>
              ) : lastUpdated ? (
                <>Updated {timeAgo(lastUpdated)}{isSnapshot ? " · cached" : ""}</>
              ) : ""}
            </div>
          </div>
          <button
            type="button"
            onClick={() => fetchData(true)}
            disabled={refreshing}
            data-testid="dashboard-refresh"
            title="Reload KPIs from server"
            className="inline-flex items-center gap-1.5 px-4 py-2.5 text-[11px] font-bold uppercase tracking-wider rounded-lg bg-[#2563EB] text-white hover:bg-[#1D4ED8] disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-colors whitespace-nowrap"
          >
            <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
          <button onClick={() => openTab("/approvals")} data-testid="approvals-tile"
            className="relative bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow px-4 sm:px-5 py-3 flex items-center gap-3 group flex-1 sm:flex-none min-w-[210px]">
            <div className="w-12 h-12 rounded-full bg-orange-50 border border-orange-100 flex items-center justify-center shrink-0">
              <Inbox size={22} className="text-[#F97316]" />
            </div>
            <div className="text-left">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#F97316] font-bold">Pending Approvals</div>
              <div className="text-2xl font-black text-slate-900 leading-none mt-0.5 tabular-nums">{k.pending_approvals}</div>
              <div className="text-[10px] text-[#F97316] mt-0.5 group-hover:underline">Open Approvals Inbox <ArrowUpRight size={10} className="inline" /></div>
            </div>
          </button>
          {/* ONE (Official Note Entry) — pending-with-me shortcut.
              Tile appears only when the user has access to the ONE
              module (the /one/dashboard/summary call would 403 otherwise
              and oneSummary stays null). */}
          {oneSummary && (
            <button onClick={() => openTab("/one")} data-testid="one-pending-tile"
              className="relative bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow px-5 py-3 flex items-center gap-3 group">
              <div className="w-12 h-12 rounded-full bg-indigo-50 border border-indigo-100 flex items-center justify-center shrink-0">
                <ClipboardList size={22} className="text-indigo-600" />
              </div>
              <div className="text-left">
                <div className="text-[10px] uppercase tracking-[0.18em] text-indigo-600 font-bold">ONE Pending With Me</div>
                <div className="text-2xl font-black text-slate-900 leading-none mt-0.5 tabular-nums">
                  {oneSummary.tiles?.pending_with_me || 0}
                </div>
                <div className="text-[10px] text-indigo-600 mt-0.5 group-hover:underline">Open Official Notes <ArrowUpRight size={10} className="inline" /></div>
              </div>
            </button>
          )}
        </div>
      </div>

      {k.dashboard_restricted && (
        <div className="mb-4 px-3 py-2 bg-slate-100 border border-slate-200 text-[11px] text-slate-500 flex items-center gap-2 rounded-lg" data-testid="dashboard-restricted-note">
          <EyeOff size={12} /> Some dashboard sections are hidden by your administrator.
        </div>
      )}

      {/* Hero KPI row — first two cards are FY-scoped Sales metrics.
          Sprint 2026-Feb — SI tile now surfaces Basic (Taxable) + GST +
          Total instead of Basic-only. */}
      {show("kpi_tiles") && (
      <div className="grid grid-cols-1 min-[480px]:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 mb-6">
        <SalesInvoiceKPI k={k} testid="kpi-turnover" />
        <KPI title={`Debtors · FY ${k.current_fy}`} value={INR(k.debtors_outstanding)} hint={`True outstanding · net of receipts, CN & released retention`} tone="amber" icon={Wallet} testid="kpi-debtors" />
        <KPI title="Creditors Outstanding" value={INR(k.creditors_outstanding)} hint="Pending payables (all) · click for details" tone="rose" icon={Receipt} testid="kpi-creditors" onClick={() => setShowCreditors(true)} />
        <KPI title="Stock Value" value={INR(k.stock_value)} hint={`${k.stock_items_in_hand} items in hand`} tone="emerald" icon={Package} testid="kpi-stock-value" />
        <KPI
          title="Asset Value (Gross)"
          value={INR(k.total_asset_value)}
          hint={k.asset_nbv_as_on ? `NBV ${INR(k.asset_nbv)} · as on ${k.asset_nbv_as_on}` : `${k.active_assets} active assets`}
          tone="indigo"
          icon={Warehouse}
          testid="kpi-asset-value"
          onClick={() => openTab("/assets")}
        />
      </div>
      )}

      {/* Post-Payment Batches · Recon Status row (Sprint 3.9) — quick health
          read of how many posted payment batches are matched against the
          bank statement vs still pending reconciliation. Tiles click
          through to the Bank Reconciliation page so the operator can
          drill in immediately. */}
      {show("batch_recon") && (
      <BatchReconStatusRow data={k.batch_recon_status}
                            onOpen={() => openTab("/bank-reconciliation")}
                            onRefresh={() => refreshSections("batch_recon")} />
      )}

      {/* 3-Month SI strip — Basic + GST + Total per month (Sprint 2026-Feb) */}
      {show("three_month_si") && Array.isArray(k.last_3_months_si) && (
        <div className="bg-[#0B1F3A] text-white rounded-xl shadow-sm mb-6 p-5" data-testid="three-month-si-strip">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-amber-300 font-bold">Sales Invoice · Basic + GST + Total</div>
              <div className="font-display text-lg font-black leading-none mt-1">Last 3 Months — Quick Pulse</div>
            </div>
            <span className="text-[10px] text-amber-200/80 uppercase tracking-wider inline-flex items-center gap-2">All Amounts in ₹
              <SectionRefreshBtn dark testid="refresh-three-month-si" onRefresh={() => refreshSections("three_month_si")} />
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {k.last_3_months_si.map((m, idx) => (
              <button key={m.key} type="button" data-testid={`three-month-si-${m.key}`}
                   onClick={() => setSiDrill({ ym: m.year_month, label: m.month_label })}
                   title="Click for day-wise & client-wise billing detail"
                   className={`p-4 rounded-lg border text-left w-full cursor-pointer hover:-translate-y-0.5 transition-transform ${idx === 0 ? "bg-black/30 border-[#F97316]" : "bg-white/5 border-white/10 hover:border-white/30"}`}>
                <div className={`text-[10px] uppercase tracking-[0.18em] font-bold ${idx === 0 ? "text-[#F97316]" : "text-blue-200"}`}>{m.label}</div>
                <div className="text-[11px] text-blue-200/70 mt-0.5">{m.month_label}</div>
                <div className="mt-3 space-y-1.5">
                  <div className="flex items-baseline justify-between gap-2" data-testid={`three-month-si-${m.key}-basic-row`}>
                    <div className="text-[10px] uppercase tracking-wider text-blue-200/80 font-bold">Basic</div>
                    <div className="text-sm font-black tabular-nums" data-testid={`three-month-si-${m.key}-basic`}>{INR(m.taxable)}</div>
                  </div>
                  <div className="flex items-baseline justify-between gap-2" data-testid={`three-month-si-${m.key}-gst-row`}>
                    <div className="text-[10px] uppercase tracking-wider text-blue-200/80 font-bold">GST</div>
                    <div className="text-sm font-black tabular-nums" data-testid={`three-month-si-${m.key}-gst`}>{INR(m.gst || 0)}</div>
                  </div>
                  <div className={`flex items-baseline justify-between gap-2 pt-1.5 border-t ${idx === 0 ? "border-[#F97316]/40" : "border-white/10"}`} data-testid={`three-month-si-${m.key}-total-row`}>
                    <div className={`text-[10px] uppercase tracking-wider font-bold ${idx === 0 ? "text-[#F97316]" : "text-blue-100"}`}>Total</div>
                    <div className={`text-lg font-black tabular-nums ${idx === 0 ? "text-white" : "text-white"}`} data-testid={`three-month-si-${m.key}-total`}>{INR(m.total || 0)}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {(show("monthly_sales") || show("fy_sales")) && (
      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        {/* Monthly sales bar chart */}
        {show("monthly_sales") && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm lg:col-span-2 p-5" data-testid="chart-monthly-sales">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Month-wise Sales</div>
              <div className="font-display text-xl font-black text-slate-900">Last 12 months</div>
            </div>
            <div className="flex items-center gap-2">
              <SectionRefreshBtn testid="refresh-monthly-sales" onRefresh={() => refreshSections("monthly_sales,fy_sales")} />
              <button onClick={() => openTab("/reports")} className="text-xs text-[#F97316] hover:underline font-bold">Open Reports →</button>
            </div>
          </div>
          <div className="flex items-end gap-2 h-44">
            {(k.monthly_sales || []).length === 0 ? (
              <div className="text-xs text-slate-400">No sales yet.</div>
            ) : k.monthly_sales.map(m => (
              <div key={m.month} className="flex-1 flex flex-col items-center gap-1 group cursor-default">
                <div className="text-[9px] font-bold text-slate-500 opacity-0 group-hover:opacity-100">{INR(m.amount)}</div>
                <div className="w-full rounded-t bg-gradient-to-t from-[#2563EB] to-[#60A5FA]" style={{ height: `${Math.max(4, (m.amount / maxMonthly) * 160)}px` }} />
                <div className="text-[9px] text-slate-500 font-mono">{m.month.slice(2)}</div>
              </div>
            ))}
          </div>
        </div>
        )}

        {/* FY-wise stacked */}
        {show("fy_sales") && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5" data-testid="chart-fy-sales">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">FY-wise Sales</div>
          <div className="font-display text-xl font-black text-slate-900 mb-3">Year over year</div>
          <div className="space-y-2.5">
            {(k.fy_sales || []).length === 0 ? <div className="text-xs text-slate-400">No data.</div> :
              k.fy_sales.map(f => (
                <div key={f.fy}>
                  <div className="flex items-center justify-between text-[11px] mb-0.5">
                    <span className="font-bold text-slate-700">FY {f.fy}</span>
                    <span className="font-mono text-slate-900 font-bold">{INR(f.amount)}</span>
                  </div>
                  <div className="h-2 bg-slate-100">
                    <div className="h-full bg-gradient-to-r from-[#F97316] to-[#FF8533]" style={{ width: `${Math.max(2, (f.amount / maxFY) * 100)}%` }} />
                  </div>
                </div>
              ))
            }
          </div>
        </div>
        )}
      </div>
      )}

      {/* Jul-2026 — Monthly Total Expenditure (Purchase sections) replaced
          the Pending Receivables aging card (client spec). Payables stays.
          Monthly Total Receipts (Bulk Receipts) sits between them. */}
      {(show("monthly_expenditure") || show("monthly_receipts") || show("pending_payables")) && (
      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        {show("monthly_expenditure") && <MonthlyExpenditureCard rows={k.monthly_expenditure || []} fy={k.current_fy} onRefresh={() => refreshSections("monthly_expenditure")} />}
        {show("monthly_receipts") && <MonthlyReceiptsCard rows={k.monthly_receipts || []} fy={k.current_fy} onRefresh={() => refreshSections("monthly_receipts")} />}
        {show("pending_payables") && <AgingCard title="Pending Payables" data={k.pending_payables_aging} total={k.creditors_outstanding} accent="rose" testid="aging-payables" onRefresh={() => refreshSections("pending_payables")} />}
      </div>
      )}

      {/* Jul-2026 — Retention Ageing (held with clients, by head × age) */}
      {show("retention_ageing") && <RetentionAgeingWidget openTab={openTab} />}
      {/* Jul-2026 — Retention by Invoice Month (user spec, sits right below Ageing) */}
      {show("retention_invoice_month") && <RetentionByInvoiceMonthWidget openTab={openTab} />}

      {/* Project Cost Control — Sprint 1 (Budget vs Committed vs Consumed) */}
      {show("project_cost_control") && <ProjectCostControlCard data={costCtl} onOpen={() => openTab("/projects")} onRefresh={refreshCostCtl} />}

      {/* Salary — month-on-month payments table (last 12 months) */}
      {show("salary_monthly") && <SalaryMonthlyTable rows={k.salary_monthly || []} onOpen={() => openTab("/purchase/salary")} onRefresh={() => refreshSections("salary_monthly")} />}

      {/* Sprint 4.4 — Warehouse Operations Strip */}
      {show("warehouse_ops") && <WarehouseOpsRow widgets={whWidgets} health={whHealth} openTab={openTab} INR={INR} onRefresh={refreshWarehouse} />}

      {/* Jul-2026 — Bank Reconciliation segregation widget (ERP-reconciled
          vs approved exceptions are never merged into one figure) */}
      {show("bank_recon") && <BankReconWidget openTab={openTab} />}

      {/* Counts strip */}
      {show("counts_strip") && (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MiniTile testid="tile-clients" label="Clients" value={k.clients_count} icon={Users} onClick={() => openTab("/clients")} />
        <MiniTile testid="tile-vendors" label="Vendors" value={k.vendors_count} icon={HardHat} onClick={() => openTab("/vendors")} />
        <MiniTile testid="tile-projects" label="Active Projects" value={k.active_projects} icon={FolderKanban} onClick={() => openTab("/projects")} />
        <MiniTile testid="tile-assets" label="Active Assets" value={k.active_assets} icon={Briefcase} onClick={() => openTab("/assets")} />
      </div>
      )}

      {/* Jun-2026 — Creditors Outstanding drill-down modal */}
      {showCreditors && <CreditorsOutstandingModal onClose={() => setShowCreditors(false)} />}
      {/* Sep-2026 — SI month drill-down modal */}
      {siDrill && <SiMonthDrillModal ym={siDrill.ym} label={siDrill.label} onClose={() => setSiDrill(null)} />}
    </div>
  );
}

// Jul-2026 — Retention Ageing widget: PENDING retention balances held with
// clients, head × age-bucket, so release requests are never forgotten.
// Self-fetching; hides silently when the user lacks retention access or
// there is nothing pending.
const AGE_TONES = ["text-slate-700", "text-slate-700", "text-amber-700", "text-orange-700", "text-rose-700"];
function RetentionAgeingWidget({ openTab }) {
  const [d, setD] = useState(null);
  const [flt, setFlt] = useState({ client_id: "", project_id: "" });
  const [pick, setPick] = useState({ clients: [], projects: [] });
  // Jun-2026 — click a head row to drill into invoice-created-month details.
  const [drillHead, setDrillHead] = useState("");
  const [drill, setDrill] = useState(null);
  const filtered = !!(flt.client_id || flt.project_id);

  const toggleDrill = (head) => {
    if (drillHead === head) { setDrillHead(""); setDrill(null); return; }
    setDrillHead(head);
    setDrill(null);
    api.get("/retentions/head-invoice-months", {
      params: {
        head,
        ...(flt.client_id && { client_id: flt.client_id }),
        ...(flt.project_id && { project_id: flt.project_id }),
      },
    }).then((r) => setDrill(r.data)).catch(() => setDrill({ months: [] }));
  };

  useEffect(() => {
    Promise.all([api.get("/clients/picker"), api.get("/projects-picker")])
      .then(([c, p]) => setPick({
        clients: (c.data || []).slice().sort((x, y) => (x.name || "").localeCompare(y.name || "")),
        projects: p.data || [],
      })).catch(() => {});
  }, []);
  const loadAgeing = () => api.get("/retentions/ageing", {
    params: {
      ...(flt.client_id && { client_id: flt.client_id }),
      ...(flt.project_id && { project_id: flt.project_id }),
    },
  }).then(r => setD(r.data)).catch(() => setD(null));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAgeing(); setDrillHead(""); setDrill(null); }, [flt]);

  // Hide entirely only when unfiltered AND empty (no access / nothing held).
  // When a filter is active we must keep the controls visible to reset it.
  if (!d || (!(d.rows || []).length && !filtered)) return null;
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-100 mb-6 overflow-hidden" data-testid="dash-retention-ageing">
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-[#7C3AED] text-white flex items-center justify-center"><Hourglass size={15} /></div>
          <div>
            <div className="font-display text-sm font-black text-slate-900">Retention Ageing</div>
            <div className="text-[10px] text-slate-400">Held with clients · {d.invoices} invoice(s) · oldest {d.oldest_days} days</div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <SectionRefreshBtn testid="refresh-retention-ageing" onRefresh={loadAgeing} />
          <select data-testid="ret-age-filter-client" value={flt.client_id}
            onChange={(e) => setFlt((f) => ({ ...f, client_id: e.target.value, project_id: "" }))}
            className="border border-slate-200 rounded px-2 py-1.5 text-[11px] bg-white focus:outline-none focus:border-violet-500 w-40">
            <option value="">All Clients</option>
            {pick.clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <div className="w-48" data-testid="ret-age-filter-project">
            <SearchableSelect
              testid="ret-age-filter-project-select"
              value={flt.project_id}
              onChange={(id) => setFlt((f) => ({ ...f, project_id: id || "" }))}
              items={pick.projects.filter((p) => {
                if (!flt.client_id) return true;
                const cl = pick.clients.find((c) => c.id === flt.client_id);
                return !p.client_name || !cl || p.client_name === cl.name;
              })}
              getLabel={(p) => p.project_name || ""}
              getSubLabel={(p) => [p.client_name, p.branch_name].filter(Boolean).join(" · ")}
              getSearchText={(p) => `${p.project_name || ""} ${p.project_code || ""} ${p.client_name || ""}`}
              placeholder="All Projects"
              searchPlaceholder="Search project…"
            />
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Total Pending</div>
            <div className="text-sm font-black tabular-nums text-violet-700" data-testid="ret-age-total">{INR(d.grand_total)}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Open Requests</div>
            <div className="text-sm font-black tabular-nums text-slate-900" data-testid="ret-age-open">
              {d.open_requests.count}{d.open_requests.count > 0 ? ` · ${INR(d.open_requests.amount)}` : ""}
            </div>
          </div>
          <button onClick={() => openTab("/receivable-retentions")} data-testid="dash-retention-ageing-open"
            className="text-[10px] uppercase tracking-wider font-bold text-blue-600 hover:text-blue-800">Open →</button>
        </div>
      </div>
      {!(d.rows || []).length ? (
        <div className="px-5 py-6 text-center text-xs text-slate-400" data-testid="ret-age-empty">
          No pending retention for this selection.
          <button onClick={() => setFlt({ client_id: "", project_id: "" })} data-testid="ret-age-clear-filters"
            className="ml-2 text-blue-600 hover:text-blue-800 font-bold uppercase text-[10px] tracking-wider">Clear filters</button>
        </div>
      ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-50 text-slate-500">
              <th className="text-left px-5 py-2 text-[10px] uppercase tracking-wider font-bold">Head</th>
              {d.bucket_labels.map((b, i) => (
                <th key={b} className={`text-right px-4 py-2 text-[10px] uppercase tracking-wider font-bold ${i >= 3 ? "text-rose-500" : ""}`}>{b}</th>
              ))}
              <th className="text-right px-5 py-2 text-[10px] uppercase tracking-wider font-bold">Total</th>
            </tr>
          </thead>
          <tbody>
            {d.rows.map((r) => (
              <Fragment key={r.head}>
              <tr className="border-t border-slate-50" data-testid={`ret-age-row-${r.head}`}>
                <td className="px-5 py-2 font-semibold text-slate-800">
                  <button onClick={() => toggleDrill(r.head)} data-testid={`ret-age-head-${r.head}`}
                    title="Click for invoice-month-wise details"
                    className="inline-flex items-center gap-1 text-left hover:text-violet-700 hover:underline">
                    {drillHead === r.head ? <ChevronDown size={12} className="shrink-0 text-violet-600" /> : <ChevronRight size={12} className="shrink-0 text-slate-400" />}
                    {r.head}
                  </button>
                  {r.oldest_days > 90 && <span className="ml-2 text-[9px] uppercase font-bold text-rose-600 bg-rose-50 border border-rose-200 px-1 py-0.5">{r.oldest_days}d old</span>}
                </td>
                {r.buckets.map((v, i) => (
                  <td key={i} className={`px-4 py-2 text-right tabular-nums ${v > 0 ? AGE_TONES[i] + " font-bold" : "text-slate-300"}`}>
                    {v > 0 ? INR(v) : "—"}
                  </td>
                ))}
                <td className="px-5 py-2 text-right tabular-nums font-black text-slate-900">{INR(r.total)}</td>
              </tr>
              {drillHead === r.head && (
                <tr className="border-t border-violet-100 bg-violet-50/40" data-testid={`ret-age-drill-${r.head}`}>
                  <td colSpan={d.bucket_labels.length + 2} className="px-5 py-3">
                    {!drill ? (
                      <div className="text-[11px] text-slate-400">Loading invoice months…</div>
                    ) : !(drill.months || []).length ? (
                      <div className="text-[11px] text-slate-400">No pending invoices for this head.</div>
                    ) : (
                      <div className="space-y-2">
                        <div className="text-[10px] uppercase tracking-wider font-bold text-violet-700">
                          {r.head} — by invoice month · {drill.invoice_count} invoice(s) · {INR(drill.grand_total)}
                        </div>
                        <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                          {drill.months.map((m) => (
                            <div key={m.month} className="border border-violet-200 bg-white rounded p-2" data-testid={`ret-age-drill-month-${m.month}`}>
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-[11px] font-black text-slate-800">{m.label}</span>
                                <span className="text-[11px] font-black tabular-nums text-violet-700">{INR(m.total)}</span>
                              </div>
                              {m.invoices.map((inv) => (
                                <div key={inv.invoice_id} className="flex items-center justify-between gap-2 text-[10px] py-0.5 border-t border-slate-50">
                                  <span className="min-w-0">
                                    <span className="font-bold text-slate-700">{inv.invoice_number}</span>
                                    <span className="text-slate-400"> · {inv.invoice_date}</span>
                                    <span className="block text-slate-400 truncate">{[inv.client_name, inv.project_name].filter(Boolean).join(" · ")}</span>
                                  </span>
                                  <span className="tabular-nums font-bold text-slate-700 shrink-0">{INR(inv.pending)}</span>
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
              )}
              </Fragment>
            ))}
            <tr className="border-t border-slate-200 bg-slate-50/60">
              <td className="px-5 py-2 text-[10px] uppercase tracking-wider font-bold text-slate-500">Total</td>
              {d.col_totals.map((v, i) => (
                <td key={i} className={`px-4 py-2 text-right tabular-nums font-black ${v > 0 ? AGE_TONES[i] : "text-slate-300"}`}>
                  {v > 0 ? INR(v) : "—"}
                </td>
              ))}
              <td className="px-5 py-2 text-right tabular-nums font-black text-violet-700">{INR(d.grand_total)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}

// Jul-2026 — Retention by Invoice Month widget (user spec): PENDING
// retention balances pivoted by the SOURCE INVOICE's month (invoice_date),
// head-wise. Mirrors the Retention Ageing card directly above it.
function RetentionByInvoiceMonthWidget({ openTab }) {
  const [d, setD] = useState(null);
  const [flt, setFlt] = useState({ client_id: "", project_id: "" });
  const [pick, setPick] = useState({ clients: [], projects: [] });
  const filtered = !!(flt.client_id || flt.project_id);

  useEffect(() => {
    Promise.all([api.get("/clients/picker"), api.get("/projects-picker")])
      .then(([c, p]) => setPick({
        clients: (c.data || []).slice().sort((x, y) => (x.name || "").localeCompare(y.name || "")),
        projects: p.data || [],
      })).catch(() => {});
  }, []);
  const loadByMonth = () => api.get("/retentions/by-invoice-month", {
    params: {
      ...(flt.client_id && { client_id: flt.client_id }),
      ...(flt.project_id && { project_id: flt.project_id }),
    },
  }).then(r => setD(r.data)).catch(() => setD(null));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadByMonth(); }, [flt]);

  if (!d || (!(d.rows || []).length && !filtered)) return null;
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-100 mb-6 overflow-hidden" data-testid="dash-retention-invoice-month">
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-[#0EA5E9] text-white flex items-center justify-center"><CalendarDays size={15} /></div>
          <div>
            <div className="font-display text-sm font-black text-slate-900">Retention · Invoice Month Wise</div>
            <div className="text-[10px] text-slate-400">Pending retention by invoice date · {d.invoices} invoice(s)</div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <SectionRefreshBtn testid="refresh-retention-invoice-month" onRefresh={loadByMonth} />
          <select data-testid="ret-mon-filter-client" value={flt.client_id}
            onChange={(e) => setFlt((f) => ({ ...f, client_id: e.target.value, project_id: "" }))}
            className="border border-slate-200 rounded px-2 py-1.5 text-[11px] bg-white focus:outline-none focus:border-sky-500 w-40">
            <option value="">All Clients</option>
            {pick.clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <div className="w-48">
            <SearchableSelect
              testid="ret-mon-filter-project-select"
              value={flt.project_id}
              onChange={(id) => setFlt((f) => ({ ...f, project_id: id || "" }))}
              items={pick.projects.filter((p) => {
                if (!flt.client_id) return true;
                const cl = pick.clients.find((c) => c.id === flt.client_id);
                return !p.client_name || !cl || p.client_name === cl.name;
              })}
              getLabel={(p) => p.project_name || ""}
              getSubLabel={(p) => [p.client_name, p.branch_name].filter(Boolean).join(" · ")}
              getSearchText={(p) => `${p.project_name || ""} ${p.project_code || ""} ${p.client_name || ""}`}
              placeholder="All Projects"
              searchPlaceholder="Search project…"
            />
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Total Pending</div>
            <div className="text-sm font-black tabular-nums text-sky-700" data-testid="ret-mon-total">{INR(d.grand_total)}</div>
          </div>
          <button onClick={() => openTab("/receivable-retentions")} data-testid="dash-retention-month-open"
            className="text-[10px] uppercase tracking-wider font-bold text-blue-600 hover:text-blue-800">Open →</button>
        </div>
      </div>
      {!(d.rows || []).length ? (
        <div className="px-5 py-6 text-center text-xs text-slate-400" data-testid="ret-mon-empty">
          No pending retention for this selection.
          <button onClick={() => setFlt({ client_id: "", project_id: "" })} data-testid="ret-mon-clear-filters"
            className="ml-2 text-blue-600 hover:text-blue-800 font-bold uppercase text-[10px] tracking-wider">Clear filters</button>
        </div>
      ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-50 text-slate-500">
              <th className="text-left px-5 py-2 text-[10px] uppercase tracking-wider font-bold">Invoice Month</th>
              <th className="text-right px-4 py-2 text-[10px] uppercase tracking-wider font-bold">Invoices</th>
              {d.heads.map((h) => (
                <th key={h} className="text-right px-4 py-2 text-[10px] uppercase tracking-wider font-bold">{h}</th>
              ))}
              <th className="text-right px-5 py-2 text-[10px] uppercase tracking-wider font-bold">Total</th>
            </tr>
          </thead>
          <tbody>
            {d.rows.map((r) => (
              <tr key={r.month} className="border-t border-slate-50" data-testid={`ret-mon-row-${r.month}`}>
                <td className="px-5 py-2 font-semibold text-slate-800">{r.label}</td>
                <td className="px-4 py-2 text-right tabular-nums text-slate-500">{r.invoices}</td>
                {d.heads.map((h) => {
                  const v = r.by_head[h] || 0;
                  return (
                    <td key={h} className={`px-4 py-2 text-right tabular-nums ${v > 0 ? "text-slate-700 font-bold" : "text-slate-300"}`}>
                      {v > 0 ? INR(v) : "—"}
                    </td>
                  );
                })}
                <td className="px-5 py-2 text-right tabular-nums font-black text-slate-900">{INR(r.total)}</td>
              </tr>
            ))}
            <tr className="border-t border-slate-200 bg-slate-50/60">
              <td className="px-5 py-2 text-[10px] uppercase tracking-wider font-bold text-slate-500">Total</td>
              <td className="px-4 py-2 text-right tabular-nums font-black text-slate-700">{d.invoices}</td>
              {d.heads.map((h) => {
                const v = d.col_totals[h] || 0;
                return (
                  <td key={h} className={`px-4 py-2 text-right tabular-nums font-black ${v > 0 ? "text-slate-900" : "text-slate-300"}`}>
                    {v > 0 ? INR(v) : "—"}
                  </td>
                );
              })}
              <td className="px-5 py-2 text-right tabular-nums font-black text-sky-700">{INR(d.grand_total)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}

// Jul-2026 — Bank Reconciliation segregation widget. Displays the five
// mandated figures with DR/CR split: ERP Batch Reconciled, Approved
// Exception (Book Entry Pending), Total Accounted, Pending Approval,
// Unmatched. Hidden silently when the user lacks bank recon access.
function BankReconWidget({ openTab }) {
  const [s, setS] = useState(null);
  const load = () => api.get("/bank-recon/exceptions/summary").then(r => setS(r.data)).catch(() => setS(null));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  if (!s) return null;
  const rows = [
    ["ERP Batch Reconciled", s.erp_reconciled_total, "text-emerald-700"],
    ["Approved Exception · Book Entry Pending", s.approved_exception_pending_book_entry, "text-purple-700"],
    ["Total Accounted For", s.total_accounted, "text-slate-900"],
    ["Exception Approval Pending", s.exception_approval_pending, "text-amber-700"],
    ["Unmatched & Unapproved", s.unmatched, "text-rose-700"],
  ];
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-100 mb-6 overflow-hidden" data-testid="dash-bank-recon-widget">
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-[#0B1F3A] text-white flex items-center justify-center"><Banknote size={15} /></div>
          <div>
            <div className="font-display text-sm font-black text-slate-900">Bank Reconciliation</div>
            <div className="text-[10px] text-slate-400">Segregated · as on {s.as_on}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <SectionRefreshBtn testid="refresh-bank-recon-widget" onRefresh={load} />
          <button onClick={() => openTab("/bank-reconciliation")} data-testid="dash-bank-recon-open"
            className="text-[10px] uppercase tracking-wider font-bold text-blue-600 hover:text-blue-800">Open →</button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 divide-x divide-slate-100">
        {rows.map(([label, b, cls]) => (
          <div key={label} className="px-4 py-3">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 leading-tight min-h-[26px]">{label}</div>
            <div className={`text-[11px] tabular font-bold mt-1 ${cls}`}>DR {INR(b?.DR || 0)}</div>
            <div className={`text-[11px] tabular font-bold ${cls}`}>CR {INR(b?.CR || 0)}</div>
            <div className="text-[10px] text-slate-400">{b?.count || 0} txn(s)</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Per-tone icon-bubble (left-aligned colored circle in the reference).
// Card body itself stays white with a subtle shadow — matches the new spec.
const TONE = {
  blue:    { bubble: "bg-[#2563EB] text-white",  rail: "bg-[#2563EB]" },
  amber:   { bubble: "bg-[#F97316] text-white",  rail: "bg-[#F97316]" },
  rose:    { bubble: "bg-rose-500 text-white",   rail: "bg-rose-500"  },
  emerald: { bubble: "bg-emerald-500 text-white", rail: "bg-emerald-500" },
  violet:  { bubble: "bg-violet-500 text-white",  rail: "bg-violet-500" },
  navy:    { bubble: "bg-[#0B1F3A] text-white",   rail: "bg-[#0B1F3A]"  },
  slate:   { bubble: "bg-slate-400 text-white",   rail: "bg-slate-400"  },
  indigo:  { bubble: "bg-indigo-500 text-white",  rail: "bg-indigo-500" },
};

function KPI({ title, value, hint, tone = "blue", icon: Icon, testid, onClick }) {
  const t = TONE[tone] || TONE.blue;
  const Tag = onClick ? "button" : "div";
  const clickable = onClick ? "cursor-pointer hover:-translate-y-0.5 transition-transform text-left w-full" : "";
  return (
    <Tag
      onClick={onClick}
      data-testid={testid}
      className={`relative bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden ${clickable}`}
    >
      {/* 3-px coloured rail on the left edge so each tile is immediately scannable */}
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${t.rail}`} />
      <div className="flex items-center gap-3 p-4 pl-5">
        <div className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${t.bubble}`}>
          {Icon && <Icon size={20} />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500 font-bold leading-tight whitespace-normal break-words">{title}</div>
          {/* Sep-2026 — same font + one-line auto-fit as the Turnover card */}
          <FitAmount text={value} base="text-base" align="left"
            className="text-slate-900 mt-0.5 leading-tight tracking-tight"
            testid={testid ? `${testid}-value` : undefined} />
          <div className="text-[10px] text-slate-500 mt-0.5 truncate">{hint}</div>
        </div>
      </div>
    </Tag>
  );
}

// Sep-2026 — 3-Month SI strip drill-down: day/date-wise + client-wise
// billing detail for the clicked month (same semantics as the strip).
function SiMonthDrillModal({ ym, label, onClose }) {
  const [data, setData] = useState(null);
  const [view, setView] = useState("date"); // 'date' | 'client'
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get("/dashboard/si-month-drill", { params: { month: ym } })
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load"));
  }, [ym]);
  const t = data?.totals || {};
  const th = "px-2.5 py-1.5 text-left text-[10px] uppercase tracking-wider font-bold text-slate-500 whitespace-nowrap";
  const td = "px-2.5 py-1.5 whitespace-nowrap";
  const num = "px-2.5 py-1.5 text-right tabular-nums whitespace-nowrap";
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="si-month-drill-modal">
        <div className="flex items-start justify-between px-5 py-4 border-b border-slate-100">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Sales Invoice · Drill-down</div>
            <div className="font-display text-lg font-black text-slate-900 leading-tight">{label} — Billing Detail</div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              {t.count ?? "…"} invoice(s) · Basic {INR(t.basic || 0)} · GST {INR(t.gst || 0)} · <span className="font-bold text-blue-700">Total {INR(t.total || 0)}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex rounded-lg border border-slate-200 overflow-hidden text-[11px] font-bold">
              <button data-testid="si-drill-view-date" onClick={() => setView("date")}
                className={`px-3 py-1.5 ${view === "date" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>Date-wise</button>
              <button data-testid="si-drill-view-client" onClick={() => setView("client")}
                className={`px-3 py-1.5 ${view === "client" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>Client-wise</button>
            </div>
            <button data-testid="si-drill-close" onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1"><X size={18} /></button>
          </div>
        </div>
        <div className="overflow-auto flex-1">
          {err && <div className="p-6 text-sm text-rose-600">{err}</div>}
          {!data && !err && <div className="p-6 text-sm text-slate-400">Loading…</div>}
          {data && view === "client" && (
            <table className="w-full text-xs" data-testid="si-drill-client-table">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className={th}>Client</th><th className={`${th} text-right`}>Invoices</th>
                  <th className={`${th} text-right`}>Basic</th><th className={`${th} text-right`}>GST</th>
                  <th className={`${th} text-right`}>Total</th>
                </tr>
              </thead>
              <tbody>
                {data.clients.map((c) => (
                  <tr key={c.client_name} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className={td} title={c.client_name}><span className="font-semibold">{c.client_name}</span></td>
                    <td className={num}>{c.count}</td>
                    <td className={num}>{INR(c.basic)}</td>
                    <td className={num}>{INR(c.gst)}</td>
                    <td className={`${num} font-bold text-blue-700`}>{INR(c.total)}</td>
                  </tr>
                ))}
                {!data.clients.length && <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-400">No invoices in this month.</td></tr>}
              </tbody>
            </table>
          )}
          {data && view === "date" && (
            <table className="w-full text-xs" data-testid="si-drill-date-table">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className={th}>Invoice No</th><th className={th}>Client</th><th className={th}>Project</th>
                  <th className={th}>Status</th>
                  <th className={`${th} text-right`}>Basic</th><th className={`${th} text-right`}>GST</th>
                  <th className={`${th} text-right`}>Total</th>
                </tr>
              </thead>
              <tbody>
                {data.days.map((day) => (
                  <FragmentRows key={day.date} day={day} rows={data.rows.filter((r) => r.date === day.date)} td={td} num={num} />
                ))}
                {!data.rows.length && <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400">No invoices in this month.</td></tr>}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function FragmentRows({ day, rows, td, num }) {
  const d = new Date(day.date + "T00:00:00");
  const nice = isNaN(d) ? day.date : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", weekday: "short" });
  return (
    <>
      <tr className="bg-slate-100/80 border-y border-slate-200" data-testid={`si-drill-day-${day.date}`}>
        <td className="px-2.5 py-1.5 font-bold text-slate-700" colSpan={4}>{nice} · {day.count} invoice(s)</td>
        <td className={`${num} font-bold`}>{INR(day.basic)}</td>
        <td className={`${num} font-bold`}>{INR(day.gst)}</td>
        <td className={`${num} font-black text-blue-700`}>{INR(day.total)}</td>
      </tr>
      {rows.map((r) => (
        <tr key={r.invoice_number + r.client_name} className="border-b border-slate-50 hover:bg-slate-50">
          <td className={`${td} font-mono`}>{r.invoice_number}</td>
          <td className={`${td} max-w-[220px] truncate`} title={r.client_name}>{r.client_name}</td>
          <td className={`${td} max-w-[180px] truncate`} title={r.project_name}>{r.project_name}</td>
          <td className={td}><StatusBadge status={r.status} /></td>
          <td className={num}>{INR(r.basic)}</td>
          <td className={num}>{INR(r.gst)}</td>
          <td className={`${num} font-bold`}>{INR(r.total)}</td>
        </tr>
      ))}
    </>
  );
}

// Jun-2026 — Creditors Outstanding drill-down: bill-level breakdown of the
// KPI tile with search + Excel export. Data comes from
// /dashboard/creditors-outstanding (same math as the tile).
const BUCKET_TONE = {
  "0-30":  "bg-emerald-50 text-emerald-700 border-emerald-200",
  "31-60": "bg-amber-50 text-amber-700 border-amber-200",
  "61-90": "bg-orange-50 text-orange-700 border-orange-200",
  ">90":   "bg-rose-50 text-rose-700 border-rose-200",
};

function CreditorsOutstandingModal({ onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get("/dashboard/creditors-outstanding")
      .then(r => setData(r.data))
      .catch(() => toast.error("Could not load creditors detail"))
      .finally(() => setLoading(false));
  }, []);

  const downloadExcel = async () => {
    setDownloading(true);
    try {
      const r = await api.get("/dashboard/creditors-outstanding/export", { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Creditors-Outstanding-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch {
      toast.error("Excel download failed");
    } finally {
      setDownloading(false);
    }
  };

  const rows = (data?.rows || []).filter(r => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return [r.vendor_name, r.source_number, r.project_name, r.branch_name, r.source_module_label, r.status]
      .some(v => (v || "").toLowerCase().includes(s));
  });
  const filteredTotal = rows.reduce((a, r) => a + (r.pending_amount || 0), 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="creditors-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[88vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-slate-200 bg-slate-50">
          <div>
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-rose-500 text-white flex items-center justify-center shrink-0"><Receipt size={17} /></div>
              <div>
                <div className="font-display text-lg font-black text-slate-900 leading-tight">Creditors Outstanding — Detail</div>
                <div className="text-[11px] text-slate-500">Pending payables (bill-level) · as on {data?.as_on || "—"}</div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={downloadExcel}
              disabled={downloading || loading}
              data-testid="creditors-export-btn"
              className="inline-flex items-center gap-1.5 px-3.5 py-2 text-[11px] font-bold uppercase tracking-wider rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 shadow-sm transition-colors"
            >
              <Download size={13} className={downloading ? "animate-bounce" : ""} />
              {downloading ? "Preparing…" : "Download Excel"}
            </button>
            <button type="button" onClick={onClose} data-testid="creditors-modal-close"
              className="w-9 h-9 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 flex items-center justify-center text-slate-500 transition-colors">
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Toolbar — search + totals */}
        <div className="flex items-center justify-between gap-3 px-6 py-3 border-b border-slate-100 flex-wrap">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search vendor / doc no / project…"
              data-testid="creditors-search"
              className="pl-9 pr-3 py-2 w-72 max-w-full text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-200"
            />
          </div>
          <div className="flex items-center gap-4 text-right">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Bills</div>
              <div className="text-sm font-black text-slate-900 tabular-nums" data-testid="creditors-count">{rows.length}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Total Pending</div>
              <div className="text-sm font-black text-rose-600 tabular-nums" data-testid="creditors-total">{INR(filteredTotal)}</div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="text-center text-slate-500 py-16">Loading creditors detail…</div>
          ) : rows.length === 0 ? (
            <div className="text-center text-slate-500 py-16">No pending payables found.</div>
          ) : (
            <table className="w-full text-[12px]" data-testid="creditors-table">
              <thead className="sticky top-0 bg-[#0B1F3A] text-white z-10">
                <tr>
                  {["Doc No", "Date", "Type", "Vendor", "Project", "Branch", "Status", "Days", "Aging", "Pending ₹"].map(h => (
                    <th key={h} className={`px-3 py-2.5 text-[10px] uppercase tracking-wider font-bold whitespace-nowrap ${h === "Pending ₹" || h === "Days" ? "text-right" : "text-left"}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={`${r.source_number}-${i}`} className="border-b border-slate-100 hover:bg-rose-50/40 transition-colors">
                    <td className="px-3 py-2 font-mono font-semibold text-slate-800 whitespace-nowrap max-w-[140px] overflow-hidden text-ellipsis" title={r.source_number || ""}>{r.source_number || "—"}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-slate-600">{r.source_date || "—"}</td>
                    <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{r.source_module_label}</td>
                    <td className="px-3 py-2 font-semibold text-slate-800">{r.vendor_name || "—"}</td>
                    <td className="px-3 py-2 text-slate-600">{r.project_name || "—"}</td>
                    <td className="px-3 py-2 text-slate-600">{r.branch_name || "—"}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${r.status === "Posted" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-slate-50 text-slate-600 border-slate-200"}`}>{r.status}</span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-600">{r.days_outstanding}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${BUCKET_TONE[r.aging_bucket] || ""}`}>{r.aging_bucket}</span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono font-bold text-slate-900 tabular-nums whitespace-nowrap">{INR(r.pending_amount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="sticky bottom-0 bg-slate-50 border-t-2 border-slate-300">
                <tr>
                  <td colSpan={9} className="px-3 py-2.5 text-right text-[10px] uppercase tracking-wider font-bold text-slate-500">Total</td>
                  <td className="px-3 py-2.5 text-right font-mono font-black text-rose-600 tabular-nums">{INR(filteredTotal)}</td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Sprint 2026-Feb — TurnOver KPI tile.
 *
 * Renamed from "Sales Invoice · FY {fy}" to "TurnOver · FY {fy}".
 * Formula: (SI − CN) + DN, matching Ind-AS Revenue from Operations
 * and the Company / Branch / Vertical P&L aggregators. Only Approved /
 * Posted docs count (drafts excluded).
 *
 * Displays a stacked triplet:
 *   • Basic  — Σ SI.subtotal − Σ CN.subtotal + Σ DN.subtotal
 *   • GST    — same, using cgst+sgst+igst
 *   • Total  — same, using total_amount (post-round-up)
 */
// Sep-2026 — KPI amounts must never wrap or clip: auto-shrinks the font
// until the formatted amount fits its row on ONE line (re-fits on resize).
function FitAmount({ value, text, base, className = "", testid, align = "right" }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const fit = () => {
      el.style.fontSize = "";
      let size = parseFloat(getComputedStyle(el).fontSize) || 16;
      while (el.scrollWidth > el.clientWidth + 0.5 && size > 9) {
        size -= 0.5;
        el.style.fontSize = `${size}px`;
      }
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el.parentElement || el);
    return () => ro.disconnect();
  }, [value, text]);
  return (
    <div ref={ref} data-testid={testid}
      className={`min-w-0 flex-1 ${align === "right" ? "text-right" : "text-left"} whitespace-nowrap overflow-hidden font-display font-black tabular-nums ${base} ${className}`}>
      {text ?? INR(value)}
    </div>
  );
}

function SalesInvoiceKPI({ k, testid }) {
  const t = TONE.blue;
  const basic = k.total_turnover_taxable_fy ?? k.total_turnover_taxable ?? 0;
  const gst   = k.total_turnover_gst_fy ?? 0;
  const total = k.total_turnover_total_fy ?? (basic + gst);
  return (
    <div data-testid={testid}
         className="relative bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${t.rail}`} />
      <div className="flex items-start gap-3 p-4 pl-5">
        <div className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${t.bubble}`}>
          <TrendingUp size={20} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500 font-bold leading-tight"
               title="Turnover = (Sales Invoices − Credit Notes) + Debit Notes · Approved/Posted only">
            TurnOver · FY {k.current_fy}
          </div>
          <div className="mt-2 space-y-1">
            <div className="flex items-baseline justify-between gap-2" data-testid="kpi-si-basic-row">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold shrink-0">Basic</div>
              <FitAmount value={basic} base="text-sm" className="text-slate-900" testid="kpi-si-basic" />
            </div>
            <div className="flex items-baseline justify-between gap-2" data-testid="kpi-si-gst-row">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold shrink-0">GST</div>
              <FitAmount value={gst} base="text-sm" className="text-slate-900" testid="kpi-si-gst" />
            </div>
            <div className="flex items-baseline justify-between gap-2 border-t border-slate-100 pt-1" data-testid="kpi-si-total-row">
              <div className="text-[10px] uppercase tracking-wider text-blue-700 font-bold shrink-0">Total</div>
              <FitAmount value={total} base="text-base" className="text-blue-700" testid="kpi-si-total" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Jun-2026 — date-wise drill-down for the Monthly Expenditure / Receipts
// cards. Clicking a month row opens this modal with day-by-day details.
function MonthDrillModal({ kind, month, onClose }) {
  const isExp = kind === "expenditure";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("date"); // receipts only: date | client
  useEffect(() => {
    api.get(`/dashboard/monthly-${isExp ? "expenditure" : "receipts"}/drill`, { params: { month } })
      .then((r) => setData(r.data))
      .catch((e) => { toast.error(e?.response?.data?.detail || "Could not load month details"); onClose(); })
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [month]);
  const rows = data?.rows || [];
  const clients = data?.clients || [];
  const t = data?.totals || {};
  const fmtDay = (d) => {
    try { return new Date(d + "T00:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", weekday: "short" }); }
    catch { return d; }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="month-drill-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[88vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-2">
            <div className={`w-9 h-9 rounded-full text-white flex items-center justify-center shrink-0 ${isExp ? "bg-[#F97316]" : "bg-emerald-600"}`}>
              <CalendarDays size={17} />
            </div>
            <div>
              <div className="font-display text-lg font-black text-slate-900 leading-tight" data-testid="month-drill-title">
                {isExp ? "Expenditure" : "Receipts"} — {data?.label || month} · Date-wise
              </div>
              <div className="text-[11px] text-slate-500">
                {isExp ? "PI + Store Rent + Salary + General Payments" : "Bulk Receipts by Receipt Date · Reversed excluded"}
              </div>
            </div>
          </div>
          <button type="button" onClick={onClose} data-testid="month-drill-close"
            className="w-9 h-9 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 flex items-center justify-center text-slate-500 transition-colors">
            <X size={16} />
          </button>
        </div>
        {!isExp && (
          <div className="flex items-center gap-1 px-6 pt-3 pb-0 border-b border-slate-200 bg-white">
            {[["date", "Date-wise"], ["client", "Client-wise"]].map(([k, lbl]) => (
              <button key={k} type="button" data-testid={`month-drill-tab-${k}`}
                onClick={() => setView(k)}
                className={`px-3 py-1.5 text-[11px] font-black uppercase tracking-wider border-b-2 -mb-px transition-colors ${view === k ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-400 hover:text-slate-600"}`}>
                {lbl}
              </button>
            ))}
          </div>
        )}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-slate-400 text-sm gap-2"><RefreshCw size={15} className="animate-spin" /> Loading…</div>
          ) : !isExp && view === "client" ? (
            clients.length === 0 ? (
              <div className="py-16 text-center text-slate-400 text-sm" data-testid="month-drill-client-empty">No records for this month.</div>
            ) : (
              <table className="w-full text-xs" data-testid="month-drill-client-table">
                <thead className="sticky top-0 bg-white shadow-sm">
                  <tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-200">
                    <th className="text-left px-4 py-2">Client</th>
                    <th className="text-right px-3 py-2"># Receipts</th>
                    <th className="text-right px-3 py-2">Total Receipts{data?.receipt_month_label ? ` — ${data.receipt_month_label}` : ""}</th>
                    <th className="text-right px-3 py-2"># SI</th>
                    <th className="text-right px-4 py-2">Total Invoices (SI){data?.si_month_label ? ` — ${data.si_month_label}` : ""}</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map((c, i) => (
                    <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`drill-client-${i}`}>
                      <td className="px-4 py-1.5 text-slate-700 font-bold max-w-[260px] truncate" title={c.client}>{c.client}</td>
                      <td className="px-3 py-1.5 text-right font-mono tabular-nums text-slate-500">{c.receipt_count || "—"}</td>
                      <td className="px-3 py-1.5 text-right font-mono tabular-nums font-bold text-emerald-700">{c.receipts ? INR(c.receipts) : "—"}</td>
                      <td className="px-3 py-1.5 text-right font-mono tabular-nums text-slate-500">{c.si_count || "—"}</td>
                      <td className="px-4 py-1.5 text-right font-mono tabular-nums font-bold text-slate-800">{c.si_total ? INR(c.si_total) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="sticky bottom-0 bg-slate-50 border-t-2 border-slate-300">
                  <tr>
                    <td className="px-4 py-2 text-[10px] uppercase tracking-wider font-black text-slate-500">Month Total</td>
                    <td className="px-3 py-2 text-right font-mono font-black tabular-nums text-slate-500">{t.count || 0}</td>
                    <td className="px-3 py-2 text-right font-mono font-black tabular-nums text-emerald-700" data-testid="drill-client-total-receipts">{INR(t.amount)}</td>
                    <td className="px-3 py-2 text-right font-mono font-black tabular-nums text-slate-500">{t.si_count || 0}</td>
                    <td className="px-4 py-2 text-right font-mono font-black tabular-nums text-slate-900" data-testid="drill-client-total-si">{INR(t.si_total)}</td>
                  </tr>
                </tfoot>
              </table>
            )
          ) : rows.length === 0 ? (
            <div className="py-16 text-center text-slate-400 text-sm" data-testid="month-drill-empty">No records for this month.</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-white shadow-sm">
                <tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-200">
                  <th className="text-left px-4 py-2">Doc No</th>
                  {isExp ? (
                    <>
                      <th className="text-left px-3 py-2">Source</th>
                      <th className="text-left px-3 py-2">Vendor / Branch</th>
                      <th className="text-right px-3 py-2">Taxable</th>
                      <th className="text-right px-3 py-2">GST</th>
                      <th className="text-right px-4 py-2">Incl. GST</th>
                    </>
                  ) : (
                    <>
                      <th className="text-left px-3 py-2">Client</th>
                      <th className="text-left px-3 py-2">Mode</th>
                      <th className="text-left px-3 py-2">Status</th>
                      <th className="text-right px-4 py-2">Amount</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {rows.map((day) => (
                  <Fragment key={day.date}>
                    <tr className="bg-slate-100/80 border-b border-slate-200" data-testid={`drill-day-${day.date}`}>
                      <td className="px-4 py-1.5 font-black text-slate-700" colSpan={isExp ? 3 : 4}>
                        {fmtDay(day.date)} <span className="font-normal text-slate-400">· {day.count} voucher{day.count > 1 ? "s" : ""}</span>
                      </td>
                      {isExp ? (
                        <>
                          <td className="px-3 py-1.5 text-right font-mono font-bold tabular-nums">{INR(day.taxable)}</td>
                          <td className="px-3 py-1.5 text-right font-mono font-bold tabular-nums">{INR(day.gst)}</td>
                          <td className="px-4 py-1.5 text-right font-mono font-black tabular-nums text-slate-900">{INR(day.total)}</td>
                        </>
                      ) : (
                        <td className="px-4 py-1.5 text-right font-mono font-black tabular-nums text-slate-900">{INR(day.amount)}</td>
                      )}
                    </tr>
                    {day.vouchers.map((v, i) => (
                      <tr key={`${day.date}-${i}`} className="border-b border-slate-50 hover:bg-slate-50/60">
                        <td className="px-4 py-1.5 pl-7 font-mono text-slate-600 whitespace-nowrap">{v.doc_no || "—"}</td>
                        {isExp ? (
                          <>
                            <td className="px-3 py-1.5 text-slate-500">{v.source}</td>
                            <td className="px-3 py-1.5 text-slate-600 max-w-[180px] truncate" title={v.party}>{v.party || "—"}</td>
                            <td className="px-3 py-1.5 text-right font-mono tabular-nums text-slate-600">{INR(v.taxable)}</td>
                            <td className="px-3 py-1.5 text-right font-mono tabular-nums text-slate-600">{INR(v.gst)}</td>
                            <td className="px-4 py-1.5 text-right font-mono tabular-nums font-bold text-slate-800">{INR(v.total)}</td>
                          </>
                        ) : (
                          <>
                            <td className="px-3 py-1.5 text-slate-600 max-w-[200px] truncate" title={v.party}>{v.party || "—"}</td>
                            <td className="px-3 py-1.5 text-slate-500">{v.mode || "—"}</td>
                            <td className="px-3 py-1.5"><span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-600 border border-slate-200">{v.status || "—"}</span></td>
                            <td className="px-4 py-1.5 text-right font-mono tabular-nums font-bold text-slate-800">{INR(v.amount)}</td>
                          </>
                        )}
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
              <tfoot className="sticky bottom-0 bg-slate-50 border-t-2 border-slate-300">
                <tr>
                  <td className="px-4 py-2 text-[10px] uppercase tracking-wider font-black text-slate-500" colSpan={isExp ? 3 : 4}>
                    Month Total · {t.count || 0} voucher{(t.count || 0) !== 1 ? "s" : ""}
                  </td>
                  {isExp ? (
                    <>
                      <td className="px-3 py-2 text-right font-mono font-black tabular-nums" data-testid="drill-total-taxable">{INR(t.taxable)}</td>
                      <td className="px-3 py-2 text-right font-mono font-black tabular-nums">{INR(t.gst)}</td>
                      <td className="px-4 py-2 text-right font-mono font-black tabular-nums text-[#F97316]" data-testid="drill-total">{INR(t.total)}</td>
                    </>
                  ) : (
                    <td className="px-4 py-2 text-right font-mono font-black tabular-nums text-emerald-700" data-testid="drill-total">{INR(t.amount)}</td>
                  )}
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// Jul-2026 — Monthly Total Expenditure (client spec): all months of the
// active FY, Taxable + Incl-GST, sourced from Purchase sections (PI +
// Store Rent + Salary + General Payments).
function MonthlyExpenditureCard({ rows, fy, onRefresh }) {
  const nowKey = new Date().toISOString().slice(0, 7);
  const [drillMonth, setDrillMonth] = useState(null);
  const tTax = rows.reduce((s, r) => s + (r.taxable || 0), 0);
  const tTot = rows.reduce((s, r) => s + (r.total || 0), 0);
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5" data-testid="monthly-expenditure-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Total Expenditure · Purchase Sections</div>
          <div className="font-display text-xl font-black text-slate-900">Monthly — FY {fy}</div>
        </div>
        <span className="text-[10px] text-slate-400 uppercase tracking-wider inline-flex items-center gap-2">PI + Store Rent + Salary + Gen. Pay
          {onRefresh && <SectionRefreshBtn testid="refresh-monthly-expenditure" onRefresh={onRefresh} />}
        </span>
      </div>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-slate-400 border-b border-slate-100">
            <th className="text-left py-1 text-[10px] uppercase tracking-wider font-bold">Month</th>
            <th className="text-right py-1 text-[10px] uppercase tracking-wider font-bold">Taxable Amt</th>
            <th className="text-right py-1 text-[10px] uppercase tracking-wider font-bold">Incl. GST</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={3} className="py-3 text-slate-400">No purchase data yet.</td></tr>
          ) : rows.map((r) => (
            <tr key={r.month} data-testid={`exp-month-${r.month}`}
                onClick={() => setDrillMonth(r.month)}
                title="Click for date-wise details"
                className={`border-b border-slate-50 cursor-pointer hover:bg-orange-50 transition-colors ${r.month === nowKey ? "bg-orange-50/60" : ""}`}>
              <td className={`py-1 font-bold ${r.month === nowKey ? "text-[#F97316]" : "text-slate-600"}`}>{r.label}</td>
              <td className="py-1 text-right font-mono tabular-nums text-slate-700">{r.taxable ? INR(r.taxable) : "—"}</td>
              <td className="py-1 text-right font-mono tabular-nums font-bold text-slate-900">{r.total ? INR(r.total) : "—"}</td>
            </tr>
          ))}
        </tbody>
        {rows.length > 0 && (
          <tfoot>
            <tr className="border-t-2 border-slate-200">
              <td className="py-1.5 text-[10px] uppercase tracking-wider font-black text-slate-500">FY Total</td>
              <td className="py-1.5 text-right font-mono tabular-nums font-black text-slate-900" data-testid="exp-fy-taxable">{INR(tTax)}</td>
              <td className="py-1.5 text-right font-mono tabular-nums font-black text-[#F97316]" data-testid="exp-fy-total">{INR(tTot)}</td>
            </tr>
          </tfoot>
        )}
      </table>
      {drillMonth && <MonthDrillModal kind="expenditure" month={drillMonth} onClose={() => setDrillMonth(null)} />}
    </div>
  );
}

// Jul-2026 — Monthly Total Receipts (user spec): all months of the active
// FY sourced from Bulk Receipts (receipt vouchers, keyed on Receipt Date,
// Reversed vouchers excluded). Design mirrors MonthlyExpenditureCard.
function MonthlyReceiptsCard({ rows, fy, onRefresh }) {
  const nowKey = new Date().toISOString().slice(0, 7);
  const [drillMonth, setDrillMonth] = useState(null);
  const tCount = rows.reduce((s, r) => s + (r.count || 0), 0);
  const tAmt = rows.reduce((s, r) => s + (r.amount || 0), 0);
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5" data-testid="monthly-receipts-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Total Receipts · Bulk Receipts</div>
          <div className="font-display text-xl font-black text-slate-900">Monthly — FY {fy}</div>
        </div>
        <span className="text-[10px] text-slate-400 uppercase tracking-wider inline-flex items-center gap-2">By Receipt Date
          {onRefresh && <SectionRefreshBtn testid="refresh-monthly-receipts" onRefresh={onRefresh} />}
        </span>
      </div>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-slate-400 border-b border-slate-100">
            <th className="text-left py-1 text-[10px] uppercase tracking-wider font-bold">Month</th>
            <th className="text-right py-1 text-[10px] uppercase tracking-wider font-bold">Receipts</th>
            <th className="text-right py-1 text-[10px] uppercase tracking-wider font-bold">Amount</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={3} className="py-3 text-slate-400">No receipt data yet.</td></tr>
          ) : rows.map((r) => (
            <tr key={r.month} data-testid={`rcpt-month-${r.month}`}
                onClick={() => setDrillMonth(r.month)}
                title="Click for date-wise details"
                className={`border-b border-slate-50 cursor-pointer hover:bg-emerald-50 transition-colors ${r.month === nowKey ? "bg-emerald-50/60" : ""}`}>
              <td className={`py-1 font-bold ${r.month === nowKey ? "text-emerald-700" : "text-slate-600"}`}>{r.label}</td>
              <td className="py-1 text-right font-mono tabular-nums text-slate-700">{r.count || "—"}</td>
              <td className="py-1 text-right font-mono tabular-nums font-bold text-slate-900">{r.amount ? INR(r.amount) : "—"}</td>
            </tr>
          ))}
        </tbody>
        {rows.length > 0 && (
          <tfoot>
            <tr className="border-t-2 border-slate-200">
              <td className="py-1.5 text-[10px] uppercase tracking-wider font-black text-slate-500">FY Total</td>
              <td className="py-1.5 text-right font-mono tabular-nums font-black text-slate-900" data-testid="rcpt-fy-count">{tCount}</td>
              <td className="py-1.5 text-right font-mono tabular-nums font-black text-emerald-700" data-testid="rcpt-fy-amount">{INR(tAmt)}</td>
            </tr>
          </tfoot>
        )}
      </table>
      {drillMonth && <MonthDrillModal kind="receipts" month={drillMonth} onClose={() => setDrillMonth(null)} />}
    </div>
  );
}

function AgingCard({ title, data, total, accent, testid, onRefresh }) {
  const colors = accent === "amber"
    ? ["bg-amber-200", "bg-amber-400", "bg-amber-600", "bg-amber-800"]
    : ["bg-rose-200", "bg-rose-400", "bg-rose-600", "bg-rose-800"];
  const buckets = ["0-30", "31-60", "61-90", ">90"];
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">{title}</div>
          <div className="font-display text-xl font-black text-slate-900">{INR(total)}</div>
        </div>
        {onRefresh && <SectionRefreshBtn testid={`refresh-${testid}`} onRefresh={onRefresh} />}
      </div>
      <div className="space-y-2">
        {buckets.map((b, i) => {
          const v = data[b] || 0;
          const pct = total > 0 ? (v / total) * 100 : 0;
          return (
            <div key={b}>
              <div className="flex items-center justify-between text-[11px] mb-0.5">
                <span className="text-slate-600 font-bold tabular-nums">{b} days</span>
                <span className="font-mono text-slate-900">{INR(v)} <span className="text-slate-400">({pct.toFixed(0)}%)</span></span>
              </div>
              <div className="h-2 bg-slate-100">
                <div className={`h-full ${colors[i]}`} style={{ width: `${Math.max(1, pct)}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MiniTile({ label, value, icon: Icon, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="bg-white border border-slate-200 rounded-xl shadow-sm hover:border-[#F97316] hover:shadow-md transition-all p-4 text-left flex items-center justify-between">
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">{label}</div>
        <div className="font-display text-3xl font-black text-slate-900 mt-1">{value}</div>
      </div>
      {Icon && <Icon size={28} className="text-slate-300" />}
    </button>
  );
}


/**
 * ProjectPLTable — FY-scoped Profit & Loss broken down by project.
 *
 * Surfaces the `project_pnl` array returned by `/api/dashboard/kpis`. Rows
 * are sorted by profit (highest first, loss-makers last) by the backend
 * already; this component renders them in a vertically scrollable panel
 * with an in-panel search filter so every project — not just the top 10 —
 * is reachable on long tenant lists.
 *
 * Visual rules:
 *   • Positive margin → emerald colour + Trending-up icon
 *   • Negative margin → rose colour + Trending-down icon
 *   • Margin bar width = |margin_pct|, capped at 100%
 *   • Sticky table header, vertical scroll capped at ~22rem (≈10 rows)
 *   • Search filters by project name / project code / client name (case-
 *     insensitive). Totals row reflects the filtered subset so the user
 *     can quickly add up a slice (e.g. all "Metro" projects).
 */
function ProjectPLTable({ rows, fy, onOpen }) {
  const [query, setQuery] = useState("");
  const safe = Array.isArray(rows) ? rows : [];
  const q = query.trim().toLowerCase();
  const visible = q
    ? safe.filter((r) => {
        return (
          (r.project_name || "").toLowerCase().includes(q) ||
          (r.project_code || "").toLowerCase().includes(q) ||
          (r.client_name || "").toLowerCase().includes(q) ||
          (r.branch_name || "").toLowerCase().includes(q)
        );
      })
    : safe;
  const totals = visible.reduce(
    (a, r) => ({
      income: a.income + (r.income || 0),
      expense: a.expense + (r.expense || 0),
      profit: a.profit + (r.profit || 0),
    }),
    { income: 0, expense: 0, profit: 0 }
  );
  const overallMargin = totals.income > 0 ? (totals.profit / totals.income) * 100 : 0;
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm mb-6" data-testid="project-pl-table">
      <div className="flex items-center justify-between gap-4 px-4 py-3 border-b border-slate-100 flex-wrap">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">
            Project-wise Profit &amp; Loss · FY {fy || "—"}
          </div>
          <div className="font-display text-lg font-black text-slate-900 leading-tight">
            Income · Expense · Profit per Project
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* In-panel search */}
          <div className="relative">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search project, code, client…"
              className="pl-7 pr-7 py-1.5 text-xs border border-slate-300 rounded focus:outline-none focus:border-[#F97316] focus:ring-2 focus:ring-[#F97316]/20 w-56"
              data-testid="project-pl-search"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 p-0.5"
                data-testid="project-pl-search-clear"
                aria-label="Clear search"
              >
                <X size={11} />
              </button>
            )}
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
              {q ? "Filtered" : "Net"} P/L
            </div>
            <div className={`font-display text-lg font-black tabular-nums ${totals.profit >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
              {INR(totals.profit)}
            </div>
            <div className={`text-[10px] font-mono ${overallMargin >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
              {overallMargin >= 0 ? "+" : ""}{overallMargin.toFixed(1)}% margin
            </div>
          </div>
          <button
            type="button"
            onClick={onOpen}
            className="text-[10px] uppercase tracking-wider font-bold text-[#F97316] hover:underline whitespace-nowrap"
            data-testid="project-pl-open-projects"
          >
            Open Projects <ArrowUpRight size={11} className="inline -mt-0.5" />
          </button>
        </div>
      </div>
      {safe.length === 0 ? (
        <div className="p-8 text-center text-sm text-slate-400">
          No project activity in the current FY yet.
        </div>
      ) : visible.length === 0 ? (
        <div className="p-8 text-center text-sm text-slate-400" data-testid="project-pl-empty-search">
          No projects match &ldquo;<span className="font-mono text-slate-600">{query}</span>&rdquo;.
          <button
            type="button"
            onClick={() => setQuery("")}
            className="ml-2 text-[#F97316] hover:underline font-bold"
          >
            Clear search
          </button>
        </div>
      ) : (
        <div className="max-h-[22rem] overflow-y-auto overflow-x-auto" data-testid="project-pl-scroller">
          <table className="w-full min-w-[640px] text-xs">
            <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10 shadow-sm">
              <tr>
                {["Project", "Client", "Income", "Expense", "Profit / Loss", "Margin %"].map((h, i) => (
                  <th
                    key={h}
                    className={`py-2 px-3 text-[10px] uppercase tracking-wider font-bold text-slate-500 bg-slate-50 ${i >= 2 ? "text-right" : "text-left"}`}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => {
                const isLoss = r.profit < 0;
                const pillCls = isLoss
                  ? "text-rose-700 bg-rose-50 border-rose-200"
                  : "text-emerald-700 bg-emerald-50 border-emerald-200";
                const barCls = isLoss ? "bg-rose-400" : "bg-emerald-500";
                const barW = Math.min(100, Math.abs(r.margin_pct || 0));
                return (
                  <tr key={r.project_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`project-pl-row-${r.project_id}`}>
                    <td className="py-2.5 px-3">
                      <div className="font-semibold text-slate-900">{r.project_name || "—"}</div>
                      <div className="text-[10px] font-mono text-slate-500">{r.project_code || "—"}</div>
                    </td>
                    <td className="py-2.5 px-3 text-slate-700">{r.client_name || "—"}</td>
                    <td className="py-2.5 px-3 text-right tabular-nums">{INR(r.income)}</td>
                    <td className="py-2.5 px-3 text-right tabular-nums">{INR(r.expense)}</td>
                    <td className={`py-2.5 px-3 text-right tabular-nums font-bold ${isLoss ? "text-rose-700" : "text-emerald-700"}`}>
                      {isLoss ? "" : "+"}{INR(r.profit)}
                    </td>
                    <td className="py-2.5 px-3">
                      <div className="flex items-center justify-end gap-2">
                        <div className="flex-1 h-1.5 bg-slate-100 rounded overflow-hidden max-w-[60px]">
                          <div className={`h-full ${barCls}`} style={{ width: `${Math.max(2, barW)}%` }} />
                        </div>
                        <span className={`inline-block text-[10px] font-bold px-1.5 py-0.5 border rounded tabular-nums min-w-[52px] text-center ${pillCls}`}>
                          {r.margin_pct >= 0 ? "+" : ""}{(r.margin_pct || 0).toFixed(1)}%
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {/* Footer totals always shown (when at least one visible row) — reflects current filter. */}
      {visible.length > 0 && (
        <>
        <div className="hidden sm:grid border-t-2 border-slate-200 bg-slate-50 px-3 py-2.5 grid-cols-12 gap-2 text-xs font-bold items-center" data-testid="project-pl-totals">
          <div className="col-span-5 text-[11px] uppercase tracking-wider text-slate-700">
            Total · {visible.length} {visible.length === 1 ? "project" : "projects"}
            {q && safe.length !== visible.length && (
              <span className="ml-1 text-slate-400 font-normal normal-case text-[10px]">(of {safe.length})</span>
            )}
          </div>
          <div className="col-span-2 text-right tabular-nums">{INR(totals.income)}</div>
          <div className="col-span-2 text-right tabular-nums">{INR(totals.expense)}</div>
          <div className={`col-span-2 text-right tabular-nums ${totals.profit >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
            {totals.profit >= 0 ? "+" : ""}{INR(totals.profit)}
          </div>
          <div className="col-span-1 text-right tabular-nums text-[#F97316]">
            {overallMargin >= 0 ? "+" : ""}{overallMargin.toFixed(1)}%
          </div>
        </div>
        {/* Mobile-compact totals (Aug-2026 mobile pass) */}
        <div className="sm:hidden border-t-2 border-slate-200 bg-slate-50 px-3 py-2.5 text-xs font-bold space-y-1" data-testid="project-pl-totals-mobile">
          <div className="text-[10px] uppercase tracking-wider text-slate-700">
            Total · {visible.length} {visible.length === 1 ? "project" : "projects"}
          </div>
          <div className="flex justify-between"><span className="text-slate-500 font-normal">Income</span><span className="tabular-nums">{INR(totals.income)}</span></div>
          <div className="flex justify-between"><span className="text-slate-500 font-normal">Expense</span><span className="tabular-nums">{INR(totals.expense)}</span></div>
          <div className="flex justify-between"><span className="text-slate-500 font-normal">Profit / Loss</span>
            <span className={`tabular-nums ${totals.profit >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{totals.profit >= 0 ? "+" : ""}{INR(totals.profit)} · {overallMargin >= 0 ? "+" : ""}{overallMargin.toFixed(1)}%</span>
          </div>
        </div>
        </>
      )}
    </div>
  );
}

/**
 * ProjectCostControlCard — Sprint 1: Budget vs Committed vs Consumed
 *
 * The single-pane truth for project P&L health. Backed by the
 * `project_cost_events` ledger so:
 *   • Committed = PI_Committed events (procurement signed, not yet consumed)
 *   • Consumed  = Material_Consumed + Direct_Expense + Return_Reversal
 *                (the realised Actual cost — what already hit P&L)
 *   • Variance  = Budget − Consumed (positive = under-budget, negative = over)
 *
 * The 6-column tile strip shows tenant-wide totals; the table below ranks
 * projects by ABSOLUTE variance so both over- and under-spent projects
 * surface at the top, and the table scrolls vertically inside the card
 * (sticky header) so operators can browse every project without leaving
 * the dashboard. Server-side capped at 500 rows.
 *
 * Sprint 1 ships a zero-budget stub when no `project_budgets` row exists
 * yet — Sprint 2 will add the budget maintenance UI.
 */
function ProjectCostControlCard({ data, onOpen, onRefresh }) {
  const [query, setQuery] = useState("");
  if (!data) {
    return null;
  }
  const t = data.totals || {};
  const allRows = Array.isArray(data.top_by_variance) ? data.top_by_variance : [];
  // Client-side search across project name, code, and client name. The
  // dashboard now ships ALL projects (sorted by |variance| desc, capped
  // server-side at 500). The card scrolls vertically inside its own
  // container so operators can browse without leaving the dashboard.
  const q = query.trim().toLowerCase();
  const rows = q ? allRows.filter((r) =>
    (r.project_name || "").toLowerCase().includes(q)
    || (r.project_code || "").toLowerCase().includes(q)
    || (r.client_name || "").toLowerCase().includes(q),
  ) : allRows;
  const profit = t.profit_loss || 0;
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm mb-6"
         data-testid="project-cost-control-card">
      <div className="flex items-center justify-between gap-4 px-4 py-3 border-b border-slate-100 flex-wrap">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">
            Project Cost Control · FY {data.fy_code || "Current"}
          </div>
          <div className="font-display text-lg font-black text-slate-900 leading-tight">
            Budget · Committed · Consumed · Revenue · Current P&amp;L · Expected P&amp;L
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {onRefresh && <SectionRefreshBtn testid="refresh-project-cost-control" onRefresh={onRefresh} />}
          <div className="relative">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search project / code / client…"
              className="pl-7 pr-7 py-1.5 border border-slate-300 text-xs focus:border-[#F97316] focus:outline-none w-64 rounded"
              data-testid="project-cost-control-search"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                aria-label="Clear search"
                data-testid="project-cost-control-search-clear"
              >
                <X size={12} />
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={onOpen}
            className="text-[10px] uppercase tracking-wider font-bold text-[#F97316] hover:underline whitespace-nowrap"
            data-testid="project-cost-control-open-projects"
          >
            Open Projects <ArrowUpRight size={11} className="inline -mt-0.5" />
          </button>
        </div>
      </div>

      {/* 5-tile strip — tenant-wide totals */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-px bg-slate-100">
        <CostKPI label="Budget" value={t.budgeted} tone="slate"
                 hint={`${t.projects_with_budget || 0}/${t.project_count || 0} configured`}
                 testid="cc-kpi-budgeted"
                 notConfigured={(t.projects_with_budget || 0) === 0} />
        <CostKPI label="Committed" value={t.committed} tone="amber"
                 hint={`Open: ${IN0(t.open_commitments || 0)}`} testid="cc-kpi-committed" />
        <CostKPI label="Consumed" value={t.consumed} tone="rose"
                 hint="Hit P&L" testid="cc-kpi-consumed" />
        <CostKPI label="Revenue" value={t.revenue_billed} tone="slate"
                 hint="Approved SI − CN" testid="cc-kpi-revenue" />
        <CostKPI label="Current P&L"
                 value={profit}
                 tone={profit >= 0 ? "emerald" : "rose"}
                 hint="Revenue − Consumed"
                 testid="cc-kpi-pnl"
                 sign />
        <CostKPI label="Expected P&L"
                 value={t.expected_profit}
                 tone={(t.expected_profit || 0) >= 0 ? "emerald" : "rose"}
                 hint="Rev − (Cons + Open)"
                 testid="cc-kpi-expected-pnl"
                 sign />
      </div>

      {/* Project rows — full list sorted by |variance|, vertical scroll
          inside the card so the operator can see every project without
          leaving the dashboard. Sticky thead keeps column labels visible
          while scrolling. */}
      {allRows.length === 0 ? (
        <div className="p-8 text-center text-sm text-slate-400">
          No project cost events posted yet. Approve a PI or Vendor Bill
          tagged with a project to start the ledger.
        </div>
      ) : rows.length === 0 ? (
        <div className="p-8 text-center text-sm text-slate-400"
             data-testid="project-cost-control-no-results">
          No projects match &ldquo;{query}&rdquo;. Try a different code, name, or client.
        </div>
      ) : (
        <div className="overflow-x-auto overflow-y-auto max-h-[60vh]"
             data-testid="project-cost-control-scroller">
          <table className="w-full text-xs" data-testid="project-cost-control-table">
            <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10 shadow-[0_1px_0_0_rgb(226,232,240)]">
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-600 font-bold">
                <th className="px-3 py-2 bg-slate-50">Project</th>
                <th className="px-3 py-2 text-right bg-slate-50">Budget</th>
                <th className="px-3 py-2 text-right bg-slate-50">Committed</th>
                <th className="px-3 py-2 text-right bg-slate-50">Consumed</th>
                <th className="px-3 py-2 text-right bg-slate-50">Revenue</th>
                <th className="px-3 py-2 text-right bg-slate-50">P/L</th>
                <th className="px-3 py-2 text-right bg-slate-50">Variance</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const pos = (r.profit_loss || 0) >= 0;
                const hasBud = r.has_budget !== false && r.budgeted !== null;
                const vpos = (r.variance || 0) >= 0;
                return (
                  <tr key={r.project_id}
                      className="border-b border-slate-100 hover:bg-slate-50"
                      data-testid={`cc-row-${r.project_code || r.project_id}`}>
                    <td className="px-3 py-2">
                      <div className="font-bold text-slate-900 truncate max-w-[200px]" title={r.project_name}>
                        {r.project_name || "—"}
                      </div>
                      <div className="text-[10px] font-mono text-slate-500">
                        {r.project_code} {r.client_name ? "· " + r.client_name : ""}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {hasBud ? IN0(r.budgeted)
                              : <span className="text-[10px] text-slate-400 italic">Not Set</span>}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-amber-700">{IN0(r.committed)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-rose-700">{IN0(r.consumed)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{IN0(r.revenue_billed)}</td>
                    <td className={`px-3 py-2 text-right tabular-nums font-bold ${pos ? "text-emerald-700" : "text-rose-700"}`}>
                      {pos ? "+" : ""}{IN0(r.profit_loss)}
                    </td>
                    <td className={`px-3 py-2 text-right tabular-nums font-bold ${
                        !hasBud ? "text-slate-300" : vpos ? "text-emerald-700" : "text-rose-700"}`}>
                      {hasBud
                        ? `${vpos ? "+" : ""}${IN0(r.variance)}`
                        : "N/A"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Compact 0-decimal INR rendering for the card's tight columns. Keeps
// the tiles a fixed width and the table grid clean.
const IN0 = (n) => {
  const num = Number(n) || 0;
  const abs = Math.abs(num);
  return `${num < 0 ? "-" : ""}₹ ${abs.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
};

function CostKPI({ label, value, hint, tone = "slate", testid, sign, notConfigured }) {
  const palette = {
    slate:   "text-slate-700",
    amber:   "text-amber-700",
    rose:    "text-rose-700",
    emerald: "text-emerald-700",
  }[tone] || "text-slate-700";
  const ToneIcon = tone === "emerald" ? TrendingUp
                : tone === "rose" ? TrendingDown
                : tone === "amber" ? AlertCircle
                : Calculator;
  let display;
  if (notConfigured) {
    display = "Not Configured";
  } else if (sign && value >= 0) {
    display = `+${IN0(value)}`;
  } else {
    display = IN0(value);
  }
  return (
    <div className="bg-white px-4 py-3" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <ToneIcon size={11} className={palette} />
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
          {label}
        </div>
      </div>
      <div className={`font-display ${notConfigured ? "text-sm" : "text-xl"} font-black tabular-nums ${notConfigured ? "text-slate-400" : palette}`}>
        {display}
      </div>
      {hint && (
        <div className="text-[10px] text-slate-400 mt-0.5 truncate" title={hint}>
          {hint}
        </div>
      )}
    </div>
  );
}



/**
 * SalaryMonthlyTable — last-12-months Salary Payment ledger.
 *
 * Renders one row per calendar month with vouchers count, gross / GST / TDS
 * and net payable. Rows with zero activity are kept (so the user can see the
 * full 12-month context). The Net column carries a tiny in-cell bar so the
 * eye is drawn to spikes.
 */
function SalaryMonthlyTable({ rows, onOpen, onRefresh }) {
  const [drillMonth, setDrillMonth] = useState(null);
  const hasRows = Array.isArray(rows) && rows.length > 0;
  // Newest month first
  const ordered = hasRows ? [...rows].reverse() : [];
  const maxNet = Math.max(1, ...ordered.map((r) => r.net || 0));
  const totals = ordered.reduce(
    (a, r) => ({
      vouchers: a.vouchers + (r.vouchers || 0),
      gross: a.gross + (r.gross || 0),
      gst: a.gst + (r.gst || 0),
      tds: a.tds + (r.tds || 0),
      net: a.net + (r.net || 0),
    }),
    { vouchers: 0, gross: 0, gst: 0, tds: 0, net: 0 }
  );
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm mb-6" data-testid="salary-monthly-table">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Salary Section · Month-on-Month</div>
          <div className="font-display text-lg font-black text-slate-900 leading-tight">Payments — Last 12 Months</div>
        </div>
        <div className="flex items-center gap-2">
          {onRefresh && <SectionRefreshBtn testid="refresh-salary-monthly" onRefresh={onRefresh} />}
          <button
            type="button"
            onClick={onOpen}
            className="text-[10px] uppercase tracking-wider font-bold text-[#F97316] hover:underline"
            data-testid="salary-monthly-open"
          >
            Open Salary Section <ArrowUpRight size={11} className="inline -mt-0.5" />
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b-2 border-slate-200">
            <tr>
              <th className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-2.5 px-4">Month</th>
              <th className="text-right text-[10px] font-bold text-slate-500 uppercase tracking-wider py-2.5 px-3">Vouchers</th>
              <th className="text-right text-[10px] font-bold text-slate-500 uppercase tracking-wider py-2.5 px-3">Gross</th>
              <th className="text-right text-[10px] font-bold text-slate-500 uppercase tracking-wider py-2.5 px-3">GST</th>
              <th className="text-right text-[10px] font-bold text-slate-500 uppercase tracking-wider py-2.5 px-3">TDS</th>
              <th className="text-right text-[10px] font-bold text-slate-500 uppercase tracking-wider py-2.5 px-3">Net Payable</th>
              <th className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-2.5 px-3 w-32">Relative</th>
            </tr>
          </thead>
          <tbody>
            {!hasRows && (
              <tr>
                <td colSpan={7} className="py-8 px-4 text-center text-slate-400 text-xs italic">
                  No salary payments raised in the last 12 months.
                </td>
              </tr>
            )}
            {ordered.map((r) => {
              const pct = r.net > 0 ? Math.max(2, (r.net / maxNet) * 100) : 0;
              const dim = r.vouchers === 0;
              return (
                <tr key={r.year_month} data-testid={`salary-row-${r.year_month}`}
                    onClick={() => setDrillMonth(r.year_month)}
                    title="Click for project-wise breakup"
                    className={`border-b border-slate-100 hover:bg-orange-50/40 cursor-pointer ${dim ? "text-slate-400" : "text-slate-800"}`}>
                  <td className="py-2.5 px-4 font-bold text-[12px] whitespace-nowrap">{r.month_label}</td>
                  <td className="py-2.5 px-3 text-right tabular-nums">{r.vouchers || "—"}</td>
                  <td className="py-2.5 px-3 text-right tabular-nums">{r.gross ? INR(r.gross) : "—"}</td>
                  <td className="py-2.5 px-3 text-right tabular-nums">{r.gst ? INR(r.gst) : "—"}</td>
                  <td className="py-2.5 px-3 text-right tabular-nums">{r.tds ? INR(r.tds) : "—"}</td>
                  <td className="py-2.5 px-3 text-right tabular-nums font-bold">{r.net ? INR(r.net) : "—"}</td>
                  <td className="py-2.5 px-3">
                    {r.net > 0 ? (
                      <div className="h-2 bg-slate-100">
                        <div className="h-full bg-gradient-to-r from-[#0B1F3A] to-[#F97316]" style={{ width: `${pct}%` }} />
                      </div>
                    ) : (
                      <div className="text-[10px] text-slate-300 italic">—</div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
          {hasRows && (
            <tfoot>
              <tr className="bg-slate-50 border-t-2 border-slate-200 font-bold">
                <td className="py-2.5 px-4 text-[11px] uppercase tracking-wider">Total (12 months)</td>
                <td className="py-2.5 px-3 text-right tabular-nums">{totals.vouchers}</td>
                <td className="py-2.5 px-3 text-right tabular-nums">{INR(totals.gross)}</td>
                <td className="py-2.5 px-3 text-right tabular-nums">{INR(totals.gst)}</td>
                <td className="py-2.5 px-3 text-right tabular-nums">{INR(totals.tds)}</td>
                <td className="py-2.5 px-3 text-right tabular-nums text-[#F97316]">{INR(totals.net)}</td>
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {drillMonth && <SalaryProjectDrillModal month={drillMonth} onClose={() => setDrillMonth(null)} />}
    </div>
  );
}

/**
 * SalaryProjectDrillModal — Aug-2026 (user request).
 * Project-wise Salary Payment totals for one salary month.
 */
function SalaryProjectDrillModal({ month, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/dashboard/salary-monthly/drill", { params: { month } })
      .then((r) => setData(r.data))
      .catch((e) => { toast.error(e?.response?.data?.detail || "Could not load month details"); onClose(); })
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [month]);
  const rows = data?.rows || [];
  const t = data?.totals || {};
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="salary-drill-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full text-white flex items-center justify-center shrink-0 bg-[#0B1F3A]">
              <CalendarDays size={17} />
            </div>
            <div>
              <div className="font-display text-lg font-black text-slate-900 leading-tight" data-testid="salary-drill-title">
                Salary — {data?.label || month} · Project-wise
              </div>
              <div className="text-[11px] text-slate-500">Attributed to the salary month on each line · Draft / Cancelled / Rejected excluded</div>
            </div>
          </div>
          <button type="button" onClick={onClose} data-testid="salary-drill-close"
            className="w-9 h-9 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 flex items-center justify-center text-slate-500 transition-colors">
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-slate-400 text-sm gap-2"><RefreshCw size={15} className="animate-spin" /> Loading…</div>
          ) : rows.length === 0 ? (
            <div className="py-16 text-center text-slate-400 text-sm" data-testid="salary-drill-empty">No salary payments for this month.</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-white shadow-sm">
                <tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-200">
                  <th className="text-left px-4 py-2">Project</th>
                  <th className="text-right px-3 py-2">Vouchers</th>
                  <th className="text-right px-3 py-2">Gross</th>
                  <th className="text-right px-3 py-2">GST</th>
                  <th className="text-right px-3 py-2">TDS</th>
                  <th className="text-right px-4 py-2">Net</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.project_id || r.project_name || i} data-testid={`salary-drill-row-${i}`}
                      className="border-b border-slate-50 hover:bg-slate-50/60">
                    <td className="px-4 py-2 font-bold text-slate-700 max-w-[260px] truncate" title={r.project_name}>{r.project_name}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-500">{r.vouchers}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-600">{INR(r.gross)}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-600">{INR(r.gst)}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-600">{INR(r.tds)}</td>
                    <td className="px-4 py-2 text-right font-mono tabular-nums font-bold text-slate-900">{INR(r.net)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="sticky bottom-0 bg-slate-50 border-t-2 border-slate-300">
                <tr>
                  <td className="px-4 py-2 text-[10px] uppercase tracking-wider font-black text-slate-500">Month Total</td>
                  <td className="px-3 py-2 text-right font-mono font-black tabular-nums">{t.vouchers || 0}</td>
                  <td className="px-3 py-2 text-right font-mono font-black tabular-nums">{INR(t.gross)}</td>
                  <td className="px-3 py-2 text-right font-mono font-black tabular-nums">{INR(t.gst)}</td>
                  <td className="px-3 py-2 text-right font-mono font-black tabular-nums">{INR(t.tds)}</td>
                  <td className="px-4 py-2 text-right font-mono font-black tabular-nums text-[#F97316]" data-testid="salary-drill-total">{INR(t.net)}</td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * BatchReconStatusRow — Sprint 3.9
 *
 * Renders 5 KPI tiles below the hero row showing Post-Payment batch
 * reconciliation health: Total · Matched · Partially Reconciled ·
 * Difference · Not Reconciled. The whole strip is clickable — any tile
 * deep-links to the Bank Reconciliation page so the operator can
 * triage immediately.
 *
 * Status semantics (matches `_enrich_batches_with_reconciliation` in
 * `server.py`):
 *   • Matched              — |Σ bank txns − batch total| ≤ ₹0.50
 *   • Partially Reconciled — bank shorter than ERP (diff < 0)
 *   • Difference           — bank exceeds ERP (diff > 0)
 *   • Not Reconciled       — zero bank txns linked to the batch
 */
function BatchReconStatusRow({ data, onOpen, onRefresh }) {
  const safe = data || { total: 0, matched: 0, partially_reconciled: 0,
                          difference: 0, not_reconciled: 0,
                          total_amount: 0, matched_amount: 0,
                          partially_reconciled_amount: 0, difference_amount: 0,
                          not_reconciled_amount: 0,
                          bank_last_txn_dates: [] };
  // % of total that's been touched at all (non-Not-Reconciled)
  const touched = safe.matched + safe.partially_reconciled + safe.difference;
  const pct = safe.total > 0 ? Math.round((touched / safe.total) * 100) : 0;
  const banks = Array.isArray(safe.bank_last_txn_dates) ? safe.bank_last_txn_dates : [];
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm mb-6"
         data-testid="dashboard-batch-recon-status">
      <div className="flex items-center justify-between gap-4 px-4 py-3 border-b border-slate-100 flex-wrap">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">
            Post-Payment Batches · Reconciliation Status
          </div>
          <div className="font-display text-lg font-black text-slate-900 leading-tight">
            Bank Recon Health — {pct}% of batches touched
            <span className="ml-2 text-xs font-bold text-slate-500" data-testid="recon-total-amount">
              · {INR(safe.total_amount)} total
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {onRefresh && <SectionRefreshBtn testid="refresh-batch-recon" onRefresh={onRefresh} />}
          <button
            type="button"
            onClick={onOpen}
            className="text-[10px] uppercase tracking-wider font-bold text-[#F97316] hover:underline whitespace-nowrap"
            data-testid="dashboard-batch-recon-open"
          >
            Open Bank Reconciliation <ArrowUpRight size={11} className="inline -mt-0.5" />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-slate-100">
        <ReconTile label="Total Batches"   value={safe.total}
                   amount={safe.total_amount}
                   tone="slate"   icon={Banknote}
                   hint="All posted batches"
                   onClick={onOpen}
                   testid="recon-tile-total" />
        <ReconTile label="Matched"         value={safe.matched}
                   amount={safe.matched_amount}
                   tone="emerald" icon={CheckCircle2}
                   hint="|Bank − ERP| ≤ ₹0.50"
                   onClick={onOpen}
                   testid="recon-tile-matched" />
        <ReconTile label="Partially Reconciled" value={safe.partially_reconciled}
                   amount={safe.partially_reconciled_amount}
                   tone="amber"   icon={CircleDashed}
                   hint="Bank < ERP"
                   onClick={onOpen}
                   testid="recon-tile-partial" />
        <ReconTile label="Difference"      value={safe.difference}
                   amount={safe.difference_amount}
                   tone="rose"    icon={CircleAlert}
                   hint="Bank > ERP"
                   onClick={onOpen}
                   testid="recon-tile-difference" />
        <ReconTile label="Not Reconciled"  value={safe.not_reconciled}
                   amount={safe.not_reconciled_amount}
                   tone="slate-strong" icon={Landmark}
                   hint="No bank txns linked"
                   onClick={onOpen}
                   testid="recon-tile-not-reconciled" />
      </div>
      {/* Sprint 4.6 — Bank-wise last transaction date strip. One pill per
          bank ledger that has at least one imported transaction; sorted
          most-recent first. Helps the operator see which banks have
          stale uploads at a glance. */}
      {banks.length > 0 && (
        <div className="border-t border-slate-100 px-4 py-3"
             data-testid="recon-bank-last-txn-strip">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">
              Bank-wise · Last Transaction Date
            </div>
            <div className="text-[10px] text-slate-400 uppercase tracking-wider">
              {banks.length} active bank{banks.length === 1 ? "" : "s"}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
            {banks.map(b => (
              <BankLastTxnPill key={b.bank_ledger_id} b={b} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * BankLastTxnPill — colour-coded by recency.
 *   • Green  · ≤ 7 days
 *   • Amber  · 8–30 days
 *   • Rose   · > 30 days or never
 * The badge is a quick UX signal for "this bank has stale uploads".
 */
function BankLastTxnPill({ b }) {
  const last = (b.last_txn_date || "").slice(0, 10);
  let toneCls = "text-rose-700 bg-rose-50 border-rose-200";
  let ageLabel = "no txns";
  if (last) {
    const d = new Date(last);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const days = Math.max(0, Math.floor((today - d) / 86400000));
    ageLabel = days === 0 ? "today"
             : days === 1 ? "yesterday"
             : `${days} day${days === 1 ? "" : "s"} ago`;
    if (days <= 7)       toneCls = "text-emerald-700 bg-emerald-50 border-emerald-200";
    else if (days <= 30) toneCls = "text-amber-700 bg-amber-50 border-amber-200";
    else                 toneCls = "text-rose-700 bg-rose-50 border-rose-200";
  }
  const display = last
    ? new Date(last).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
    : "—";
  const acct = b.account_number_tail ? ` · ****${b.account_number_tail}` : "";
  return (
    <div className={`flex items-center justify-between gap-3 px-3 py-2 border ${toneCls}`}
         data-testid={`recon-bank-pill-${b.bank_ledger_id}`}>
      <div className="min-w-0">
        <div className="text-[11px] font-bold text-slate-800 truncate" title={`${b.bank_name || b.bank_ledger_name}${acct}`}>
          {b.bank_name || b.bank_ledger_name}<span className="text-slate-500 font-normal">{acct}</span>
        </div>
        <div className="text-[10px] text-slate-500 mt-0.5">
          {b.txn_count} txn{b.txn_count === 1 ? "" : "s"} · {ageLabel}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="text-[10px] uppercase tracking-wider text-slate-400">Last</div>
        <div className="text-xs font-bold tabular-nums">{display}</div>
      </div>
    </div>
  );
}

function ReconTile({ label, value, amount, hint, tone = "slate", icon: Icon,
                     onClick, testid }) {
  // Distinct palette per status; tiles share a white surface for a
  // calm-clean look (matches the existing CostKPI tiles' visual rhythm).
  const palette = {
    slate:          "text-slate-700",
    "slate-strong": "text-slate-900",
    amber:          "text-amber-700",
    rose:           "text-rose-700",
    emerald:        "text-emerald-700",
  }[tone] || "text-slate-700";
  const iconBg = {
    slate:          "bg-slate-50 border-slate-100",
    "slate-strong": "bg-slate-100 border-slate-200",
    amber:          "bg-amber-50 border-amber-100",
    rose:           "bg-rose-50 border-rose-100",
    emerald:        "bg-emerald-50 border-emerald-100",
  }[tone] || "bg-slate-50 border-slate-100";
  return (
    <button
      type="button"
      onClick={onClick}
      className="bg-white px-4 py-4 text-left hover:bg-slate-50/70 transition-colors group"
      data-testid={testid}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <div className={`w-7 h-7 rounded-full border flex items-center justify-center ${iconBg}`}>
          <Icon size={13} className={palette} />
        </div>
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
          {label}
        </div>
      </div>
      <div className={`font-display text-2xl font-black tabular-nums ${palette}`}>
        {value ?? 0}
      </div>
      {amount !== undefined && (
        <div className={`text-[11px] font-bold tabular-nums mt-0.5 ${palette}`}
             data-testid={`${testid}-amount`}>
          {INR(amount)}
        </div>
      )}
      {hint && (
        <div className="text-[10px] text-slate-400 mt-0.5 truncate" title={hint}>
          {hint}
        </div>
      )}
    </button>
  );
}



// ============================================================================
// Sprint 4.4 Phase D — Warehouse Operations dashboard row
// ============================================================================
// Renders 7 warehouse KPI cards + the Health tile in a single strip directly
// above the existing counts strip. The Health tile fans out into 7 health
// metrics — clicking any metric deep-links to the relevant warehouse page so
// the operator can drill into the offending records immediately.
// `widgets` and `health` are independent fetches — either can be null without
// breaking the other.
function WarehouseOpsRow({ widgets, health, openTab, INR, onRefresh }) {
  if (!widgets && !health) return null;
  const w = widgets || {};
  const highest = w.highest_stock_warehouse;
  const lowest  = w.lowest_stock_warehouse;
  return (
    <div className="mb-6" data-testid="dashboard-warehouse-row">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Stores · Warehouse</div>
          <div className="font-display text-xl font-black text-slate-900">Warehouse Operations</div>
        </div>
        <div className="flex items-center gap-2">
          {onRefresh && <SectionRefreshBtn testid="refresh-warehouse-ops" onRefresh={onRefresh} />}
          <button onClick={() => openTab("/warehouse-movements")}
            className="text-xs text-[#F97316] hover:underline font-bold">
            Movement Ledger →
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <WarehouseKPI testid="wh-total" icon={Warehouse} tone="blue"
          label="Total Warehouses" value={w.total_warehouses ?? 0}
          hint="Active warehouses across all branches"
          onClick={() => openTab("/company-setup")} />
        <WarehouseKPI testid="wh-inv-value" icon={Package} tone="emerald"
          label="Total Inventory Value" value={INR(w.total_inventory_value ?? 0)}
          hint={`${w.total_warehouses ?? 0} warehouses · live stock`}
          onClick={() => openTab("/inventory")} />
        <WarehouseKPI testid="wh-asset-value" icon={Briefcase} tone="violet"
          label="Total Asset Value" value={INR(w.total_asset_value ?? 0)}
          hint="Active assets, excludes Scrapped / Sold"
          onClick={() => openTab("/assets")} />
        <WarehouseKPI testid="wh-pending-repairs" icon={Wrench}
          tone={(w.pending_repairs ?? 0) > 0 ? "amber" : "slate"}
          label="Pending Repairs" value={w.pending_repairs ?? 0}
          hint="Awaiting completion at vendor"
          onClick={() => openTab("/asset-lifecycle")} />
        <WarehouseKPI testid="wh-highest" icon={TrendingUp} tone="emerald"
          label="Highest Stock Warehouse"
          value={highest ? INR(highest.value) : "—"}
          hint={highest ? `${highest.warehouse_name}${highest.branch_name ? ` · ${highest.branch_name}` : ""}` : "No warehouses with stock"}
          onClick={() => openTab("/inventory")} />
        <WarehouseKPI testid="wh-lowest" icon={TrendingDown} tone="rose"
          label="Lowest Stock Warehouse"
          value={lowest ? INR(lowest.value) : "—"}
          hint={lowest ? `${lowest.warehouse_name}${lowest.branch_name ? ` · ${lowest.branch_name}` : ""}` : "No warehouses with stock"}
          onClick={() => openTab("/inventory")} />
        <WarehouseKPI testid="wh-todays-movements" icon={Activity} tone="blue"
          label="Today's Movements" value={w.todays_movements ?? 0}
          hint="PI / IV / RV / Transfers / Repairs"
          onClick={() => openTab("/warehouse-movements")} />
        <WarehouseKPI testid="wh-default-missing" icon={CircleAlert}
          tone={(w.branches_without_default_warehouse ?? 0) > 0 ? "rose" : "emerald"}
          label="Branches Without Default WH"
          value={w.branches_without_default_warehouse ?? 0}
          hint="Each branch should expose a default warehouse"
          onClick={() => openTab("/company-setup")} />
      </div>

      {health && <WarehouseHealthTile health={health} openTab={openTab} />}
    </div>
  );
}

function WarehouseKPI({ testid, icon: Icon, label, value, hint, onClick, tone = "blue" }) {
  const palette = {
    blue:    { bg: "bg-blue-50",    icon: "text-blue-700",    text: "text-blue-700" },
    emerald: { bg: "bg-emerald-50", icon: "text-emerald-700", text: "text-emerald-700" },
    violet:  { bg: "bg-violet-50",  icon: "text-violet-700",  text: "text-violet-700" },
    amber:   { bg: "bg-amber-50",   icon: "text-amber-700",   text: "text-amber-700" },
    rose:    { bg: "bg-rose-50",    icon: "text-rose-700",    text: "text-rose-700" },
    slate:   { bg: "bg-slate-50",   icon: "text-slate-600",   text: "text-slate-700" },
  }[tone] || { bg: "bg-slate-50", icon: "text-slate-600", text: "text-slate-700" };
  return (
    <button onClick={onClick} data-testid={testid}
      className="text-left bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow px-4 py-3">
      <div className="flex items-center gap-2 mb-1">
        <div className={`w-7 h-7 rounded-full border flex items-center justify-center ${palette.bg}`}>
          <Icon size={13} className={palette.icon} />
        </div>
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold truncate" title={label}>
          {label}
        </div>
      </div>
      <div className={`font-display text-xl font-black tabular-nums ${palette.text}`}>{value}</div>
      {hint && (
        <div className="text-[10px] text-slate-400 mt-0.5 truncate" title={hint}>{hint}</div>
      )}
    </button>
  );
}

function WarehouseHealthTile({ health, openTab }) {
  const items = [
    { key: "branches_missing_default_warehouse",
      label: "Branches without default warehouse",
      count: (health.branches_missing_default_warehouse || []).length,
      nav: "/company-setup" },
    { key: "warehouses_with_zero_stock",
      label: "Warehouses with zero stock",
      count: (health.warehouses_with_zero_stock || []).length,
      nav: "/inventory" },
    { key: "warehouses_without_store_incharge",
      label: "Warehouses without Store In-charge",
      count: (health.warehouses_without_store_incharge || []).length,
      nav: "/company-setup" },
    { key: "inventory_records_missing_warehouse",
      label: "Inventory missing warehouse",
      count: (health.inventory_records_missing_warehouse || {}).count || 0,
      nav: "/inventory" },
    { key: "assets_missing_warehouse",
      label: "Assets missing warehouse",
      count: (health.assets_missing_warehouse || {}).count || 0,
      nav: "/assets" },
    { key: "duplicate_warehouse_codes",
      label: "Duplicate warehouse codes",
      count: (health.duplicate_warehouse_codes || []).length,
      nav: "/company-setup" },
    { key: "inactive_warehouses_with_stock",
      label: "Inactive warehouses with stock",
      count: (health.inactive_warehouses_with_stock || []).length,
      nav: "/company-setup" },
  ];
  const anyIssue = items.some(i => i.count > 0);
  return (
    <div data-testid="dashboard-warehouse-health"
      className={`rounded-xl border ${anyIssue ? "border-amber-300 bg-amber-50/40" : "border-emerald-300 bg-emerald-50/40"} px-5 py-4`}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-9 h-9 rounded-full flex items-center justify-center ${anyIssue ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
          <ShieldAlert size={16} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">Warehouse Health</div>
          <div className={`font-display text-base font-bold ${anyIssue ? "text-amber-900" : "text-emerald-900"}`}>
            {anyIssue ? "Action items detected" : "All checks passing"}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
        {items.map(it => {
          const has = it.count > 0;
          return (
            <button key={it.key} data-testid={`wh-health-${it.key}`}
              onClick={() => openTab(it.nav)}
              className={`text-left border px-3 py-2 rounded-lg transition-colors ${has ? "border-amber-300 bg-white hover:bg-amber-50" : "border-emerald-200 bg-white hover:bg-emerald-50"}`}>
              <div className="text-[9px] uppercase tracking-wider font-bold text-slate-500 truncate" title={it.label}>{it.label}</div>
              <div className={`text-lg font-black tabular-nums ${has ? "text-amber-800" : "text-emerald-700"}`}>{it.count}</div>
              <div className={`text-[10px] ${has ? "text-amber-700" : "text-emerald-700"}`}>
                {has ? "View details →" : "OK"}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// Sprint 4.11.7 — Dashboard.view permission placeholder
// Rendered in place of the real KPIs when the logged-in user's role
// lacks `dashboard.view`. We intentionally keep the chrome (tab bar,
// page header) consistent and only blank the figures, so the role
// holder is never confused about whether the page broke vs.
// whether they don't have access. All numeric tiles are flat-zero
// ("0"/"—") and a single inline banner explains the gate.
// ─────────────────────────────────────────────────────────────
function RestrictedTile({ label }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md px-4 py-3"
         data-testid={`dashboard-restricted-tile-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">{label}</div>
      <div className="text-2xl font-black tabular-nums text-slate-300 mt-1">0</div>
      <div className="text-[10px] text-slate-300 mt-0.5">—</div>
    </div>
  );
}

function DashboardRestrictedPlaceholder() {
  const { openTab } = useTabs();
  // 2026-Feb — Even when a role lacks `dashboard.view`, the Approvals
  // Inbox and Official Note Entry (ONE) shortcuts remain visible + live
  // so the user can still reach their queue. Both fetches are guarded
  // with `.catch()` so a 403 (no permission) or network hiccup silently
  // hides the shortcut instead of blocking the page.
  const [approvalsCount, setApprovalsCount] = useState(null);
  const [oneSummary, setOneSummary] = useState(null);
  useEffect(() => {
    api.get("/approvals/pending")
      .then(r => setApprovalsCount(Array.isArray(r.data) ? r.data.length : 0))
      .catch(() => setApprovalsCount(null));
    api.get("/one/dashboard/summary")
      .then(r => setOneSummary(r.data))
      .catch(() => setOneSummary(null));
  }, []);

  return (
    <div data-testid="dashboard-restricted">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-bold">
          Operations Snapshot · {new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
        </div>
        {/* Live shortcuts — Approvals + ONE. These bypass the dashboard.view
            gate because they use dedicated APIs (/approvals/pending and
            /one/dashboard/summary) that only require the caller to have
            access to those individual modules. */}
        <div className="flex items-center gap-2 flex-wrap">
          {approvalsCount !== null && (
            <button
              onClick={() => openTab("/approvals")}
              data-testid="approvals-tile"
              className="relative bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow px-5 py-3 flex items-center gap-3 group"
            >
              <div className="w-12 h-12 rounded-full bg-orange-50 border border-orange-100 flex items-center justify-center shrink-0">
                <Inbox size={22} className="text-[#F97316]" />
              </div>
              <div className="text-left">
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#F97316] font-bold">Pending Approvals</div>
                <div className="text-2xl font-black text-slate-900 leading-none mt-0.5 tabular-nums">{approvalsCount}</div>
                <div className="text-[10px] text-[#F97316] mt-0.5 group-hover:underline">Open Approvals Inbox <ArrowUpRight size={10} className="inline" /></div>
              </div>
            </button>
          )}
          {oneSummary && (
            <button
              onClick={() => openTab("/one")}
              data-testid="one-pending-tile"
              className="relative bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow px-5 py-3 flex items-center gap-3 group"
            >
              <div className="w-12 h-12 rounded-full bg-indigo-50 border border-indigo-100 flex items-center justify-center shrink-0">
                <ClipboardList size={22} className="text-indigo-600" />
              </div>
              <div className="text-left">
                <div className="text-[10px] uppercase tracking-[0.18em] text-indigo-600 font-bold">ONE Pending With Me</div>
                <div className="text-2xl font-black text-slate-900 leading-none mt-0.5 tabular-nums">
                  {oneSummary.tiles?.pending_with_me || 0}
                </div>
                <div className="text-[10px] text-indigo-600 mt-0.5 group-hover:underline">Open Official Notes <ArrowUpRight size={10} className="inline" /></div>
              </div>
            </button>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-amber-300 bg-amber-50 px-5 py-4 mb-6 flex items-start gap-3"
           data-testid="dashboard-restricted-banner">
        <div className="w-9 h-9 rounded-full bg-amber-100 border border-amber-300 flex items-center justify-center flex-shrink-0">
          <Lock size={16} className="text-amber-800" />
        </div>
        <div>
          <div className="text-sm font-bold text-amber-900">Dashboard figures are restricted for your role.</div>
          <div className="text-xs text-amber-800 mt-1 leading-relaxed">
            Your administrator has not granted the <span className="font-mono bg-amber-100 px-1.5 py-0.5 rounded text-[11px]">dashboard.view</span> permission to your role.
            All KPI tiles below are shown as zero. If you need access to operational figures, please request your administrator to enable
            <span className="font-mono bg-amber-100 px-1.5 py-0.5 rounded text-[11px]">Dashboard → View</span> in <span className="font-semibold">Administration → Users → Roles</span>.
            {" "}Your Approvals inbox and Official Notes queue above remain fully functional.
          </div>
        </div>
      </div>

      {/* Visually-matching zeroed KPI grid — the "Pending Approvals" tile
          has been removed from this list because it is now a live
          shortcut in the header row above. */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        <RestrictedTile label="Sales (MTD)" />
        <RestrictedTile label="Sales (FY)" />
        <RestrictedTile label="Receivables" />
        <RestrictedTile label="Payables" />
        <RestrictedTile label="Stock Value" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-6">
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-2">Bank Reconciliation Health</div>
          <div className="text-3xl font-black tabular-nums text-slate-300">—</div>
          <div className="text-[11px] text-slate-300 mt-1">Restricted</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-2">Project Health</div>
          <div className="text-3xl font-black tabular-nums text-slate-300">—</div>
          <div className="text-[11px] text-slate-300 mt-1">Restricted</div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-6 text-center">
        <div className="text-sm font-bold text-slate-400">Detailed KPI charts &amp; trend lines are hidden</div>
        <div className="text-xs text-slate-400 mt-1">Enable <span className="font-mono">dashboard.view</span> to populate this section.</div>
      </div>
    </div>
  );
}
